import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useMutation, useQuery } from 'convex/react'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  Send,
  Sparkles,
  TreeDeciduous,
  Download,
  HelpCircle,
  RotateCcw,
  Play,
  AlertTriangle,
  MessageSquareQuote,
  Bot,
  User as UserIcon,
  CheckCircle2,
  Lightbulb,
  Terminal,
  CornerDownLeft,
  MoreHorizontal,
  Loader2,
  Check,
} from 'lucide-react'
import {
  Button,
  Card,
  Badge,
  Loader,
  AI3CCoverageProgress,
  Markdown,
} from '@/components/common'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { useConversationStore } from '@/stores/conversationStore'
import { useConversation } from '@/hooks/useConversation'
import { formatConversationState, formatRelativeTime } from '@/utils/formatters'
import { countWords, WORD_LIMIT } from '@/utils/wordCount'
import { cn } from '@/utils/cn'
import type {
  Answer,
  AnswerStatus,
  Branch,
  ConversationState,
  Message,
  Question,
} from '@/types'
import type { ProblemOutput, SynthesisRow } from '@/types/conversation'
import type { DiffChange } from '@/types/api'
import { AAISuggestion } from '@/components/conversation/AAISuggestion'
import { BranchTabs } from '@/components/conversation/BranchTabs'
import { RoundSubmitBar } from '@/components/conversation/RoundSubmitBar'
import { PruneConfirmModal } from '@/components/conversation/PruneConfirmModal'
import { GenerateConfirmModal } from '@/components/conversation/GenerateConfirmModal'
import { ScrollNav } from '@/components/conversation/ScrollNav'
import TreeViewModal from '@/components/tree/TreeViewModal'
import ExportModal from '@/components/problem/ExportModal'
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'

const WorkspacePage = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const { isTreeViewOpen, setTreeViewOpen } = useUIStore()
  const { user } = useAuthStore()

  // Convex reactive reads. These stay live across backend writes performed
  // by the Python workers (messages, branch status, problemOutput updates).
  const project = useQuery(
    api.projects.get,
    projectId
      ? { projectId: projectId as unknown as Id<'projects'> }
      : 'skip',
  )
  const convexBranches = useQuery(
    api.branches.listByProject,
    projectId
      ? { projectId: projectId as unknown as Id<'projects'> }
      : 'skip',
  )
  const convexMessages = useQuery(
    api.messages.listByProject,
    projectId
      ? { projectId: projectId as unknown as Id<'projects'> }
      : 'skip',
  )
  const problemHistory = useQuery(
    api.projects.getProblemOutputHistory,
    projectId
      ? { projectId: projectId as unknown as Id<'projects'> }
      : 'skip',
  ) as Array<{ version: number; timestamp: number; output: ProblemOutput }> | undefined
  const restoreVersion = useMutation(api.projects.restoreProblemVersion)

  // Multi-card synthesis (spec answer #2-#7 final batch). Each call to
  // "Generate Problem Statement" creates a new row here; revisions
  // update it in place. The active card is tracked separately from
  // the active branch so the chat panel and the synthesis card can
  // coexist in the sidebar.
  const syntheses = useQuery(
    api.syntheses.listByProject,
    projectId
      ? { projectId: projectId as unknown as Id<'projects'> }
      : 'skip',
  ) as Array<SynthesisRow> | undefined
  const createSynthesis = useMutation(api.syntheses.create)
  const migrateLegacySynthesis = useMutation(api.syntheses.migrateLegacyProblemOutput)
  const [activeSynthesisId, setActiveSynthesisId] = useState<string | null>(null)

  const conv = useConversation(projectId ?? '')
  const store = useConversationStore()

  const [inputValue, setInputValue] = useState('')
  const [treeModalOpen, setTreeModalOpen] = useState(false)
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [pruneTargetId, setPruneTargetId] = useState<string | null>(null)
  const [generateModalOpen, setGenerateModalOpen] = useState(false)
  // Bumped whenever the tree modal jumps to a branch; the messages
  // scroll container watches this to reset its scroll position so the
  // user lands at the top of that branch's history (R11).
  const [jumpTick, setJumpTick] = useState(0)
  const messagesScrollRef = useRef<HTMLDivElement | null>(null)

  // -------- Multi-tab presence (Batch 7d) ------------------------------ //
  const tabIdRef = useRef<string>(
    (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2),
  )
  const sessionIdRef = useRef<Id<'sessions'> | null>(null)
  const joinTab = useMutation(api.sessions.joinTab)
  const pingTab = useMutation(api.sessions.pingTab)
  const leaveTab = useMutation(api.sessions.leaveTab)
  const activeTabs = useQuery(
    api.sessions.listActiveTabs,
    projectId ? { projectId: projectId as unknown as Id<'projects'> } : 'skip',
  ) as Array<{ _id: string; tabId?: string; lastSeenAt?: number }> | undefined
  const sessionCount = activeTabs?.length ?? 1

  useEffect(() => {
    if (!projectId || !user?.convexUserId) return
    let cancelled = false
    let intervalId: ReturnType<typeof setInterval> | null = null

    const start = async () => {
      const id = await joinTab({
        projectId: projectId as unknown as Id<'projects'>,
        userId: user.convexUserId as unknown as Id<'users'>,
        tabId: tabIdRef.current,
        userAgent: navigator.userAgent,
      })
      if (cancelled) return
      sessionIdRef.current = id as unknown as Id<'sessions'>
      intervalId = setInterval(() => {
        if (sessionIdRef.current) {
          pingTab({ sessionId: sessionIdRef.current }).catch(() => {})
        }
      }, 20_000)
    }
    void start()

    const onUnload = () => {
      if (sessionIdRef.current) {
        leaveTab({ sessionId: sessionIdRef.current }).catch(() => {})
      }
    }
    window.addEventListener('beforeunload', onUnload)

    return () => {
      cancelled = true
      if (intervalId) clearInterval(intervalId)
      window.removeEventListener('beforeunload', onUnload)
      if (sessionIdRef.current) {
        leaveTab({ sessionId: sessionIdRef.current }).catch(() => {})
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, user?.convexUserId])

  // Conflict toast: when other tabs are active and the message/branch
  // counts change without our SSE actively streaming, fire a toast.
  const lastCountsRef = useRef<{ msgs: number; branches: number } | null>(null)
  useEffect(() => {
    const msgCount = convexMessages?.length ?? 0
    const branchCount = convexBranches?.length ?? 0
    const last = lastCountsRef.current
    lastCountsRef.current = { msgs: msgCount, branches: branchCount }
    if (!last) return
    if (sessionCount <= 1) return
    if (conv.isStreaming) return
    if (msgCount > last.msgs || branchCount > last.branches) {
      toast('Project updated by another open tab', { icon: 'ℹ️', duration: 4000 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convexMessages, convexBranches])

  // Hydrate local conversation store from Convex. This runs once per
  // project change so the UI can resume from a refresh without replaying
  // SSE events.
  useEffect(() => {
    if (!projectId || !project) return
    if (store.projectId === projectId) return

    const p = project as {
      status?: string
      initialSituation?: string
      cleanedSituation?: string
      cleanerDiff?: DiffChange[]
      problemOutput?: ProblemOutput | null
      currentRound?: number
    }

    store.reset()
    store.setProjectId(projectId)

    if (p.initialSituation) store.setRawSituation(p.initialSituation)
    if (p.cleanedSituation) {
      store.setCleanedSituation(p.cleanedSituation, p.cleanerDiff ?? [])
    }
    if (p.problemOutput) store.setProblemOutput(p.problemOutput)
    if (typeof p.currentRound === 'number') {
      store.setCurrentRound(p.currentRound)
    }
    void conv.hydrateDrafts().catch(() => {})

    // Resume state heuristic. Note: we deliberately do NOT auto-jump to
    // REVIEWING when `problemOutput` exists anymore — synthesis cards
    // are now selectable from the sidebar, and the chat panel must
    // remain available so the user can keep answering / generate
    // again later (spec answer #3, final-batch).
    //   completed               -> COMPLETED (project marked done by user)
    //   has cleanedSituation    -> ANSWERING (active branches likely exist)
    //   has initialSituation    -> CONFIRM_CLEAN (waiting on user confirm)
    //   else                    -> IDLE
    if (p.status === 'completed') {
      store.setState('COMPLETED')
    } else if (p.cleanedSituation) {
      store.setState('ANSWERING')
    } else if (p.initialSituation) {
      store.setState('CONFIRM_CLEAN')
    } else {
      store.setState('IDLE')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, projectId])

  // Mirror Convex branches into the store so `store.getActiveBranch`,
  // `canGenerateProblem`, and other getters behave correctly after reload.
  useEffect(() => {
    if (!convexBranches) return
    const branches = (convexBranches as unknown as Branch[]).map((b) => ({
      ...b,
    }))
    store.setBranches(branches)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convexBranches])

  // ---- Lazy migration: legacy `projects.problemOutput` → syntheses ----
  // Triggered once per project: if the project has a problemOutput
  // (legacy single-card flow) and zero rows in the new `syntheses`
  // table, run the one-shot migration mutation that splits history
  // into individual cards. Idempotent server-side.
  const migrationFiredRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!projectId) return
    if (!project) return
    if (!syntheses) return
    if (syntheses.length > 0) return
    if (!(project as { problemOutput?: unknown })?.problemOutput) return
    if (migrationFiredRef.current.has(projectId)) return
    migrationFiredRef.current.add(projectId)
    void migrateLegacySynthesis({
      projectId: projectId as unknown as Id<'projects'>,
    }).catch((err) => {
      console.warn('Legacy synthesis migration failed', err)
    })
  }, [projectId, project, syntheses, migrateLegacySynthesis])

  // ---- Auto-select most recent synthesis on first load ----
  // Spec answer #4 (final batch): on reload, pre-select the latest
  // synthesis card if any exist; otherwise fall back to the user's
  // last branch (handled by the existing `activeBranchId` resolver).
  const synthesisAutoSelectRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!projectId) return
    if (!syntheses || syntheses.length === 0) return
    if (synthesisAutoSelectRef.current.has(projectId)) return
    synthesisAutoSelectRef.current.add(projectId)
    // Sorted ascending by ordinal in the query; pick the last one.
    const latest = syntheses[syntheses.length - 1]
    if (latest) setActiveSynthesisId(latest._id)
  }, [projectId, syntheses])

  // Track whether ANY synthesis is currently generating/revising —
  // disables the Generate button (spec answer #5: one synthesis at a
  // time per project). Also drives the streaming state for the active
  // card while OAI streams tokens in.
  const isAnySynthesisGenerating = useMemo(() => {
    return (syntheses ?? []).some(
      (s) => s.status === 'generating' || s.status === 'revising',
    )
  }, [syntheses])

  const activeSynthesis = useMemo(() => {
    if (!activeSynthesisId) return null
    return (syntheses ?? []).find((s) => s._id === activeSynthesisId) ?? null
  }, [activeSynthesisId, syntheses])

  // Mirror the full Convex message list into the store (used for export
  // and downstream analytics).
  useEffect(() => {
    if (!convexMessages) return
    store.setMessages(convexMessages as unknown as Message[])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convexMessages])

  // Auto-transition to READY_TO_GENERATE when every branch has finished
  // (terminated naturally / manually / pruned). Generate is now a
  // multi-card workflow so we can transition even if there's already
  // a synthesis on file — the user might want a fresh card after
  // adding more depth.
  useEffect(() => {
    if (!convexBranches || convexBranches.length === 0) return
    const branches = convexBranches as unknown as Array<{ status: string }>
    const allDone = branches.every((b) => b.status !== 'active')
    if (
      allDone &&
      (conv.state === 'ANSWERING' ||
        conv.state === 'AAI_ASSIST' ||
        conv.state === 'EVALUATING' ||
        conv.state === 'QUESTIONING')
    ) {
      store.setState('READY_TO_GENERATE')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convexBranches, conv.state])

  // Auto-prompt the Generate modal once every branch has terminated /
  // pruned, the user has at least one completed round, and they haven't
  // dismissed the prompt for this run.
  const branchSummary = useMemo(() => {
    const list = (convexBranches ?? []) as unknown as Array<{ status: string }>
    let active = 0
    let pruned = 0
    let completed = 0
    for (const b of list) {
      if (b.status === 'active') active += 1
      else if (b.status === 'pruned') pruned += 1
      else completed += 1
    }
    return { active, pruned, completed }
  }, [convexBranches])

  useEffect(() => {
    if (!convexBranches || convexBranches.length === 0) return
    // Only prompt if no synthesis has ever been generated; otherwise
    // assume the user knows about the Generate flow.
    if ((syntheses?.length ?? 0) > 0) return
    if (store.autoGenerateModalShown) return
    if (store.currentRound < 1) return
    if (branchSummary.active > 0) return
    setGenerateModalOpen(true)
    store.setAutoGenerateModalShown(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convexBranches, branchSummary.active, syntheses?.length, store.currentRound])

  const projectName =
    (project as { name?: string } | null | undefined)?.name ?? 'Loading…'
  const generationInProgress =
    (project as { generationInProgress?: string } | null | undefined)
      ?.generationInProgress ?? null
  const state = conv.state

  // Messages grouped by branch
  const messagesByBranch = useMemo(() => {
    const m = new Map<string, Message[]>()
    for (const raw of (convexMessages ?? []) as unknown as Message[]) {
      const arr = m.get(raw.branchId) ?? []
      arr.push(raw)
      m.set(raw.branchId, arr)
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => (a.round - b.round) || (a.orderInRound - b.orderInRound))
    }
    return m
  }, [convexMessages])

  // Active branch: prefer conv store, else first active branch from Convex,
  // else the root.
  const activeBranchId = useMemo(() => {
    if (conv.activeBranchId) return conv.activeBranchId
    const branches = (convexBranches ?? []) as Array<{
      _id: string
      status: string
      depth: number
    }>
    const active = branches.find((b) => b.status === 'active')
    if (active) return active._id
    const root = branches.find((b) => b.depth === 0)
    return root?._id ?? null
  }, [conv.activeBranchId, convexBranches])

  const activeMessages = activeBranchId
    ? messagesByBranch.get(activeBranchId) ?? []
    : []

  // The Convex branch row for the currently-viewed branch (used to show
  // a read-only banner when the user clicks a terminated/pruned branch
  // in the sidebar — those branches are intentionally absent from the
  // BranchTabs strip).
  const activeBranchObj = useMemo(() => {
    if (!activeBranchId) return null
    const branches = (convexBranches ?? []) as Array<{
      _id: string
      status: string
    }>
    return branches.find((b) => b._id === activeBranchId) ?? null
  }, [activeBranchId, convexBranches])
  const isViewingInactiveBranch =
    !!activeBranchObj && activeBranchObj.status !== 'active'

  // ----- Scroll memory + first-unanswered + auto-scroll-if-near-bottom ----
  //
  // Goals:
  // 1. When the user switches to a branch they've already viewed, restore
  //    the exact scroll position they left.
  // 2. On the first visit to a branch, scroll to the first unanswered
  //    question of the current round.
  // 3. When new messages arrive on the active branch, only auto-scroll if
  //    the user is already near the bottom (within 80px). Otherwise show
  //    a "↓ new messages" pill so they can opt-in.
  // 4. The Tree-modal "Jump to branch" action should still bump the user
  //    to that branch (it sets activeBranchId, so behavior 1 or 2 fires).

  const scrollOffsets = useRef<Map<string, number>>(new Map())
  const visitedBranches = useRef<Set<string>>(new Set())
  const lastBranchIdRef = useRef<string | null>(null)
  const lastMessageCountRef = useRef<number>(0)
  const [hasNewMessages, setHasNewMessages] = useState(false)

  const isNearBottom = (el: HTMLElement, threshold = 80) =>
    el.scrollHeight - el.scrollTop - el.clientHeight < threshold

  const scrollToBottom = (smooth = true) => {
    const el = messagesScrollRef.current
    if (!el) return
    el.scrollTo({
      top: el.scrollHeight,
      behavior: smooth ? 'smooth' : 'auto',
    })
    setHasNewMessages(false)
  }

  // Persist the outgoing branch's scroll position whenever the user
  // scrolls (cheap; native passive listener, no re-renders).
  useEffect(() => {
    const el = messagesScrollRef.current
    if (!el) return
    const handler = () => {
      if (lastBranchIdRef.current) {
        scrollOffsets.current.set(lastBranchIdRef.current, el.scrollTop)
      }
      // If the user has scrolled themselves to the bottom, dismiss the pill.
      if (isNearBottom(el, 16)) setHasNewMessages(false)
    }
    el.addEventListener('scroll', handler, { passive: true })
    return () => el.removeEventListener('scroll', handler)
  }, [])

  // Branch switch: save outgoing offset, then either restore the saved
  // offset or jump to the first unanswered question.
  useEffect(() => {
    const el = messagesScrollRef.current
    if (!el) return

    if (lastBranchIdRef.current && lastBranchIdRef.current !== activeBranchId) {
      scrollOffsets.current.set(lastBranchIdRef.current, el.scrollTop)
    }
    lastBranchIdRef.current = activeBranchId

    if (!activeBranchId) return

    // Defer to the next frame so the new branch's messages have rendered.
    const raf = requestAnimationFrame(() => {
      const saved = scrollOffsets.current.get(activeBranchId)
      if (visitedBranches.current.has(activeBranchId) && saved !== undefined) {
        el.scrollTo({ top: saved, behavior: 'auto' })
      } else {
        // First visit: try to scroll to the first unanswered question of
        // the current round; fall back to top.
        const root = el
        const allQs = Array.from(
          root.querySelectorAll<HTMLElement>('[data-question-id]'),
        )
        // Find any question whose id has no following human_answer sibling
        // with parentMessageId === that question id. Cheaper proxy: pick
        // the first qai_question whose round equals the maximum round in
        // the DOM. (The chat is sorted by round + orderInRound.)
        let target: HTMLElement | null = null
        if (allQs.length > 0) {
          const maxRound = allQs.reduce(
            (m, n) => Math.max(m, Number(n.dataset.round ?? 0)),
            0,
          )
          target =
            allQs.find((n) => Number(n.dataset.round) === maxRound) ?? allQs[0]
        }
        if (target) {
          target.scrollIntoView({ block: 'start', behavior: 'auto' })
        } else {
          el.scrollTo({ top: 0, behavior: 'auto' })
        }
        visitedBranches.current.add(activeBranchId)
      }
      setHasNewMessages(false)
      lastMessageCountRef.current = activeMessages.length
    })

    return () => cancelAnimationFrame(raf)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId, jumpTick])

  // New messages on the active branch: auto-scroll if near bottom,
  // otherwise pop the "new messages" pill.
  useEffect(() => {
    const el = messagesScrollRef.current
    if (!el || !activeBranchId) return
    const prev = lastMessageCountRef.current
    const next = activeMessages.length
    if (next > prev) {
      if (isNearBottom(el)) {
        // Use rAF so the DOM has the new bubbles before we measure again.
        requestAnimationFrame(() => scrollToBottom(true))
      } else {
        setHasNewMessages(true)
      }
    }
    lastMessageCountRef.current = next
  }, [activeMessages.length, activeBranchId])

  // Unanswered questions in the active branch (used to drive the
  // ANSWERING UI block).
  const currentQuestions: Question[] = useMemo(() => {
    if (!activeBranchId) return []
    const stored = conv.getQuestionsForBranch?.(activeBranchId) ?? []
    if (stored.length > 0) return stored
    // Fallback: pull the latest qai_question messages from Convex if the
    // local store hasn't been populated yet (e.g. after a refresh).
    const msgs = activeMessages.filter((m) => m.role === 'qai_question')
    const latestRound = msgs.reduce((max, m) => Math.max(max, m.round), 0)
    return msgs
      .filter((m) => m.round === latestRound)
      .map<Question>((m) => ({
        id: m._id,
        text: m.content,
        type: (m.questionType ?? 'open') as Question['type'],
        options: (m.questionOptions as unknown as Question['options']) ?? undefined,
        ai3cTarget: (m.ai3cElement as Question['ai3cTarget']) ?? null,
      }))
  }, [activeBranchId, activeMessages, conv])

  const handleSubmitSituation = async () => {
    if (!inputValue.trim()) return
    try {
      // First-time submission resets the revision counter; the
      // ConfirmCleanPanel bumps it on each subsequent re-clean.
      store.resetCleanerRevision()
      await conv.submitSituation(inputValue.trim())
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Submit failed')
    }
  }

  const handleConfirmClean = async (useOriginal: boolean) => {
    try {
      await conv.confirmSituation(useOriginal)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Confirm failed')
    }
  }

  const handleGenerate = async () => {
    if (!projectId) return
    if (isAnySynthesisGenerating) {
      toast.error('A synthesis is already running. Wait for it to finish first.')
      return
    }
    try {
      // Spec answer #2 (final batch): create the synthesis card FIRST
      // so it appears immediately in the sidebar. The user is then
      // auto-routed to it and watches OAI stream into the new card.
      const result = await createSynthesis({
        projectId: projectId as unknown as Id<'projects'>,
        generatedAtRound: store.currentRound,
      })
      const newId = (result?.synthesisId ?? null) as string | null
      if (newId) setActiveSynthesisId(newId)
      await conv.generateProblem(newId ?? undefined)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Generate failed')
    }
  }

  const handleSubmitRound = async () => {
    try {
      await conv.submitRound()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Round submit failed')
    }
  }

  const handleConfirmPrune = async (cascade: boolean) => {
    if (!pruneTargetId) return
    try {
      await conv.pruneBranch(pruneTargetId, cascade)
      toast.success(cascade ? 'Branch and descendants pruned.' : 'Branch pruned; children re-parented to root.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Prune failed')
    } finally {
      setPruneTargetId(null)
    }
  }

  const handleRequestRename = async (branchId: string) => {
    const next = window.prompt('Rename branch to:')
    if (!next || !next.trim()) return
    try {
      await conv.renameBranch(branchId, next.trim())
      toast.success(`Branch renamed.`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Rename failed')
    }
  }

  const handleRequestTerminate = async (branchId: string) => {
    try {
      const result = await conv.terminateBranchGated(branchId, 'user_terminated')
      if (result) toast.success('Branch terminated.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Terminate failed')
    }
  }

  const pruneTargetName = useMemo(() => {
    if (!pruneTargetId) return ''
    const b = (convexBranches ?? []).find((x) => x._id === pruneTargetId) as
      | { name?: string; thematicName?: string; userRenamedName?: string }
      | undefined
    return b?.userRenamedName || b?.thematicName || b?.name || 'this branch'
  }, [pruneTargetId, convexBranches])

  const [resetPending, setResetPending] = useState(false)

  const handleCommand = async (rawCmd: string) => {
    const input = rawCmd.trim()
    if (!input) return

    // Free-text chat: anything that doesn't start with `/` is treated as
    // an out-of-band note from the human, attached to the active branch.
    if (!input.startsWith('/')) {
      if (!activeBranchId) {
        toast.error('Open or create a branch first before adding chat notes.')
        return
      }
      try {
        await conv.freeChat(input, activeBranchId)
        toast.success('Note added to branch')
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not post chat')
      }
      return
    }

    // Strip the slash and split into verb + args.
    const stripped = input.slice(1).trim()
    if (!stripped) return
    const [head, ...rest] = stripped.split(/\s+/)
    const verb = head.toLowerCase()
    const arg = rest.join(' ').replace(/^["']|["']$/g, '').trim()

    try {
      // /rename <name>
      if (verb === 'rename') {
        if (!activeBranchId) {
          toast.error('No active branch to rename.')
          return
        }
        if (!arg) {
          toast.error('Usage: /rename New Branch Name')
          return
        }
        await conv.renameBranch(activeBranchId, arg)
        toast.success(`Branch renamed to "${arg}".`)
        return
      }

      // /kill | /terminate [reason] — gated: refuses if any current-round
      // questions for this branch are still unanswered/unskipped.
      if (verb === 'kill' || verb === 'terminate') {
        if (!activeBranchId) {
          toast.error('No active branch to terminate.')
          return
        }
        const result = await conv.terminateBranchGated(
          activeBranchId,
          arg || 'user_terminated',
        )
        if (result) toast.success('Branch terminated.')
        return
      }

      // /generate — manual OAI trigger (multi-card synthesis flow)
      if (verb === 'generate') {
        if (store.currentRound < 1) {
          toast.error('Submit at least one round of answers first.')
          return
        }
        await handleGenerate()
        return
      }

      // /branch — hint only (branching is done via the question UI)
      if (verb === 'branch') {
        toast(
          'Branching is driven by the AI. Each round of questions may create new inquiry branches automatically.',
          { icon: 'ℹ️', duration: 5000 },
        )
        return
      }

      // /retry — replay the last failed action
      if (verb === 'retry') {
        if (!conv.lastAction) {
          toast.error('Nothing to retry — no previous action recorded.')
          return
        }
        toast(`Retrying: ${conv.lastAction.type}...`, { icon: '🔄' })
        const result = await conv.retryLastAction()
        if (!result) {
          toast.error('Could not retry that action.')
        }
        return
      }

      // /reset — two-step: ask confirmation, then execute
      if (verb === 'reset') {
        if (resetPending) {
          const result = await conv.confirmReset()
          setResetPending(false)
          toast.success(result?.message || 'Project reset. Submit a new situation to start fresh.')
          return
        }
        setResetPending(true)
        toast(
          'Are you sure? This will delete all branches, messages, and the problem output.\nType /reset again to confirm.',
          { icon: '⚠️', duration: 6000 },
        )
        return
      }

      // /continue — resume from last persisted state
      if (verb === 'continue') {
        const result = await conv.continueConversation()
        if (result?.message) {
          toast.success(result.message)
        }
        return
      }

      // /help — show help text
      if (verb === 'help') {
        const result = await conv.executeCommand('help')
        if (result?.message) {
          toast(result.message, { duration: 10000, style: { whiteSpace: 'pre-line', maxWidth: '480px' } })
        }
        return
      }

      // Unknown command
      toast.error(`Unknown command: /${verb}. Type /help for a list of commands.`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Command failed')
    }
  }

  // Clear reset confirmation after 10s
  useEffect(() => {
    if (!resetPending) return
    const timer = setTimeout(() => setResetPending(false), 10000)
    return () => clearTimeout(timer)
  }, [resetPending])

  if (!projectId) {
    return (
      <div className="p-12 text-center text-slate-600 dark:text-slate-300">
        Project not found.
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      <div className="flex-1 flex flex-col">
        <WorkspaceHeader
          projectName={projectName}
          state={state}
          isStreaming={conv.isStreaming}
          isTreeViewOpen={isTreeViewOpen}
          onBack={() => navigate('/projects')}
          onToggleTree={() => setTreeViewOpen(!isTreeViewOpen)}
          onOpenTreeModal={() => setTreeModalOpen(true)}
          onCommand={handleCommand}
          canExport={state === 'REVIEWING' || state === 'COMPLETED'}
          onExport={() => setExportModalOpen(true)}
          sessionCount={sessionCount}
        />

        {generationInProgress && (
          <div className="mx-6 mt-4 inline-flex items-center self-start gap-2 px-3 py-1.5 rounded-full bg-light-icy-blue/15 dark:bg-dark-muted-teal/15 text-xs font-medium text-light-icy-blue dark:text-dark-muted-teal">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            {generationInProgress === 'cleaner' && 'Cleaning your situation in the background…'}
            {generationInProgress === 'qai' && 'Generating questions in the background — feel free to navigate away.'}
            {generationInProgress === 'oai' && 'Synthesizing the problem statement in the background…'}
            {!['cleaner', 'qai', 'oai'].includes(generationInProgress) &&
              `${generationInProgress} working in the background…`}
          </div>
        )}

        {conv.error && (
          <div className="mx-6 mt-4 p-4 rounded-xl border border-red-200 bg-red-50 dark:bg-red-500/10 dark:border-red-500/30 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-red-800 dark:text-red-200">{conv.error}</p>
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="secondary" onClick={conv.clearError}>
                  Dismiss
                </Button>
                {conv.lastAction && (
                  <Button size="sm" onClick={() => handleCommand('/retry')}>
                    Retry
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="relative flex-1 min-h-0">
          {hasNewMessages && (
            <button
              type="button"
              onClick={() => scrollToBottom(true)}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-light-icy-blue dark:bg-dark-muted-teal text-white text-xs font-medium shadow-lg hover:opacity-90"
            >
              <CornerDownLeft className="w-3 h-3 rotate-180" />
              New messages
            </button>
          )}
          <div ref={messagesScrollRef} className="absolute inset-0 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto space-y-6">
            {activeSynthesis ? (
              <SynthesisCardPanel
                synthesis={activeSynthesis}
                isStreaming={conv.isStreaming}
                streamContent={conv.streamContent}
                onBackToChat={() => setActiveSynthesisId(null)}
                onRevise={async (text) => {
                  await conv.reviseProblem(text, activeSynthesis._id, activeSynthesis.revision)
                }}
                onContinueChat={async (text) => {
                  await conv.continueDepth(text, activeSynthesis._id)
                }}
              />
            ) : (
              <>
            {isViewingInactiveBranch && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-700/40 bg-amber-50 dark:bg-amber-500/10 px-4 py-2 text-xs text-amber-800 dark:text-amber-200 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span>
                  Viewing{' '}
                  <span className="font-semibold">
                    {activeBranchObj?.status?.replace(/_/g, ' ')}
                  </span>{' '}
                  branch — read-only
                </span>
              </div>
            )}
            <MessageHistory messages={activeMessages} />

            <StateRenderer
              state={state}
              isStreaming={conv.isStreaming}
              streamContent={conv.streamContent}
              rawSituation={conv.rawSituation}
              cleanedSituation={conv.cleanedSituation}
              inputValue={inputValue}
              onInput={setInputValue}
              onSubmitSituation={handleSubmitSituation}
              onConfirmClean={handleConfirmClean}
              onGenerate={handleGenerate}
              onSubmitRound={handleSubmitRound}
              currentQuestions={currentQuestions}
              activeBranchId={activeBranchId}
              onRequestAAI={conv.requestAAIHelp}
              onRequestRename={handleRequestRename}
              onRequestTerminate={handleRequestTerminate}
              onRequestPrune={(id) => setPruneTargetId(id)}
              onSaveDraft={conv.saveDraft}
              problemOutput={conv.problemOutput}
              onRevise={conv.reviseProblem}
              onDismissAAI={conv.dismissAAI}
              branches={(convexBranches ?? []) as unknown as Array<{
                _id: string
                name?: string
                thematicName?: string
                userRenamedName?: string
                status: string
                depth: number
              }>}
              onSelectBranch={(id) => {
                setActiveSynthesisId(null)
                conv.setActiveBranch(id)
              }}
              problemHistory={problemHistory ?? []}
              onRestoreVersion={async (version) => {
                if (!projectId) return
                await restoreVersion({
                  projectId: projectId as unknown as Id<'projects'>,
                  version,
                })
              }}
              onContinueDepth={conv.continueDepth}
              cleanerDiff={store.diff}
              vaguenessIssues={store.cleanerVaguenessIssues}
              requiresClarification={store.cleanerRequiresClarification}
              onResubmitWithClarifications={async (clarified) => {
                setInputValue(clarified)
                store.bumpCleanerRevision()
                await conv.submitSituation(clarified)
              }}
              cleanerRevision={store.cleanerRevision}
            />
              </>
            )}
          </div>
          </div>
        </div>

        <CommandBar onCommand={handleCommand} />
      </div>

      <AnimatePresence>
        {isTreeViewOpen && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 400, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden"
          >
            <TreeSidebar
              branches={(convexBranches ?? []) as Array<{
                _id: string
                name?: string
                thematicName?: string
                userRenamedName?: string
                status: string
                depth: number
                localDepth?: number
                spawnedAtRound?: number
              }>}
              activeBranchId={activeBranchId}
              onSelectBranch={(id) => {
                setActiveSynthesisId(null)
                conv.setActiveBranch(id)
              }}
              onRenameBranch={conv.renameBranch}
              onTerminateBranch={async (id, reason) => {
                await conv.terminateBranchGated(id, reason)
              }}
              onPruneBranch={async (id) => setPruneTargetId(id)}
              ai3cCoverage={conv.problemOutput?.metadata?.ai3cCoverage}
              canGenerate={
                store.currentRound > 0 &&
                store.canSubmitRound() &&
                !isAnySynthesisGenerating
              }
              generateDisabledReason={
                isAnySynthesisGenerating
                  ? 'A synthesis is currently being generated. Wait for it to finish.'
                  : undefined
              }
              onGenerate={() => setGenerateModalOpen(true)}
              currentRound={store.currentRound}
              messagesByBranch={messagesByBranch}
              draftsByBranch={store.answersPerBranch}
              syntheses={syntheses ?? []}
              activeSynthesisId={activeSynthesisId}
              onSelectSynthesis={(id) => setActiveSynthesisId(id)}
              onExportSynthesis={() => setExportModalOpen(true)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {!user?.convexUserId && (
        <div className="absolute bottom-4 left-4 right-4 max-w-md mx-auto p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm shadow">
          Convex is not connected; session events will still work, but
          refresh/rehydration requires Convex.
        </div>
      )}

      <TreeViewModal
        isOpen={treeModalOpen}
        onClose={() => setTreeModalOpen(false)}
        branches={(convexBranches ?? []) as unknown as Parameters<typeof TreeViewModal>[0]['branches']}
        activeBranchId={activeBranchId}
        onSetActive={(id) => {
          conv.setActiveBranch(id)
          setJumpTick((n) => n + 1)
          setTreeModalOpen(false)
        }}
        onRename={conv.renameBranch}
        onTerminate={async (id, reason) => {
          await conv.terminateBranchGated(id, reason)
        }}
        onPrune={async (id) => {
          setPruneTargetId(id)
          setTreeModalOpen(false)
        }}
      />

      {projectId && (
        <ExportModal
          isOpen={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          projectId={projectId}
          projectName={projectName}
        />
      )}

      <PruneConfirmModal
        isOpen={!!pruneTargetId}
        branchName={pruneTargetName}
        onCancel={() => setPruneTargetId(null)}
        onConfirm={(cascade) => handleConfirmPrune(cascade)}
      />

      <GenerateConfirmModal
        isOpen={generateModalOpen}
        branchSummary={branchSummary}
        onCancel={() => setGenerateModalOpen(false)}
        onConfirm={async () => {
          setGenerateModalOpen(false)
          await handleGenerate()
        }}
      />

      <ScrollNav scrollRef={messagesScrollRef} />
    </div>
  )
}

// ---------- Header ---------- //

interface WorkspaceHeaderProps {
  projectName: string
  state: ConversationState
  isStreaming: boolean
  isTreeViewOpen: boolean
  onBack: () => void
  onToggleTree: () => void
  onOpenTreeModal: () => void
  onCommand: (cmd: string) => void
  canExport: boolean
  onExport: () => void
  sessionCount: number
}

const WorkspaceHeader = ({
  projectName,
  state,
  isStreaming,
  isTreeViewOpen,
  onBack,
  onToggleTree,
  onOpenTreeModal,
  onCommand,
  canExport,
  onExport,
  sessionCount,
}: WorkspaceHeaderProps) => {
  return (
    <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to projects"
          title="Back to projects"
          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
        </button>
        <div>
          <h1 className="font-semibold text-slate-900 dark:text-white">
            {projectName}
          </h1>
          <div className="flex items-center gap-2 mt-0.5">
            <Badge variant={getStateVariant(state)} size="sm">
              {formatConversationState(state)}
            </Badge>
            {isStreaming && <Loader size="sm" variant="dots" />}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {sessionCount > 1 && (
          <Badge variant="yellow" size="sm" className="mr-1">
            {sessionCount} sessions open
          </Badge>
        )}

        <CommandButtons onCommand={onCommand} />

        <Button
          variant={isTreeViewOpen ? 'primary' : 'secondary'}
          size="sm"
          leftIcon={<TreeDeciduous className="w-4 h-4" />}
          onClick={onToggleTree}
          title="Toggle branch list sidebar"
        >
          List
        </Button>

        <Button
          variant="secondary"
          size="sm"
          leftIcon={<TreeDeciduous className="w-4 h-4" />}
          onClick={onOpenTreeModal}
          title="Open full D3 tree visualization"
        >
          Tree
        </Button>

        {canExport && (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Download className="w-4 h-4" />}
            onClick={onExport}
          >
            Export
          </Button>
        )}
      </div>
    </div>
  )
}

const CommandButtons = ({
  onCommand,
}: {
  onCommand: (cmd: string) => void
}) => {
  const btn =
    'px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400'
  return (
    <div className="hidden sm:flex items-center gap-1 mr-2 text-xs">
      <button onClick={() => onCommand('/continue')} className={btn}>
        /continue
      </button>
      <button onClick={() => onCommand('/retry')} className={btn}>
        /retry
      </button>
      <button onClick={() => onCommand('/reset')} className={btn}>
        /reset
      </button>
      <button onClick={() => onCommand('/help')} className={btn}>
        /help
      </button>
    </div>
  )
}

// ---------- Command Bar ---------- //

const COMMAND_HINTS: Array<{ cmd: string; desc: string }> = [
  { cmd: '/continue', desc: 'Resume the conversation from the last state' },
  { cmd: '/retry', desc: 'Retry the last failed agent step' },
  { cmd: '/reset', desc: 'Reset the project (requires confirmation)' },
  { cmd: '/help', desc: 'Show available commands and tips' },
  { cmd: '/rename', desc: 'Rename the active branch: /rename My Branch' },
  { cmd: '/kill', desc: 'Terminate the active branch (gated by current round)' },
  { cmd: '/branch', desc: 'How to fork a new branch from a question' },
  { cmd: '/generate', desc: 'Synthesize the problem statement from the tree so far' },
]

const CommandBar = ({ onCommand }: { onCommand: (cmd: string) => void }) => {
  const [value, setValue] = useState('')
  const [showHints, setShowHints] = useState(false)

  const matches = useMemo(() => {
    const q = value.trim().toLowerCase()
    if (!q || !q.startsWith('/')) return []
    return COMMAND_HINTS.filter((h) => h.cmd.toLowerCase().startsWith(q))
  }, [value])

  const submit = () => {
    const trimmed = value.trim()
    if (!trimmed) return
    // Preserve the leading "/" so handleCommand can distinguish a slash
    // command from a free-text chat message destined for QAI.
    onCommand(trimmed)
    setValue('')
    setShowHints(false)
  }

  return (
    <div className="border-t border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur px-6 py-3">
      <div className="max-w-3xl mx-auto relative">
        {showHints && matches.length > 0 && (
          <div className="absolute bottom-full mb-2 left-0 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden">
            {matches.map((m) => (
              <button
                type="button"
                key={m.cmd}
                onClick={() => {
                  setValue(m.cmd + ' ')
                  setShowHints(true)
                }}
                className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700/60"
              >
                <span className="font-mono text-slate-700 dark:text-slate-300">{m.cmd}</span>
                <span className="text-xs text-slate-500 dark:text-slate-400">{m.desc}</span>
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus-within:ring-2 focus-within:ring-mint-500">
          <Terminal className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
              setShowHints(e.target.value.trim().startsWith('/'))
            }}
            onFocus={() => setShowHints(value.trim().startsWith('/'))}
            onBlur={() => setTimeout(() => setShowHints(false), 120)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                submit()
              } else if (e.key === 'Escape') {
                setShowHints(false)
              }
            }}
            placeholder='Type /command (e.g. /help) or any free-text note to attach to the active branch'
            className="flex-1 bg-transparent outline-none text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 font-mono"
            aria-label="Command input"
          />
          <button
            type="button"
            onClick={submit}
            disabled={!value.trim()}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Run command"
            title="Run command (Enter)"
          >
            <CornerDownLeft className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------- State Renderer ---------- //

interface StateRendererProps {
  state: ConversationState
  isStreaming: boolean
  streamContent: string
  rawSituation: string
  cleanedSituation: string
  inputValue: string
  onInput: (v: string) => void
  onSubmitSituation: () => void
  onConfirmClean: (useOriginal: boolean) => void
  onGenerate: () => void
  onSubmitRound: () => void | Promise<void>
  currentQuestions: Question[]
  activeBranchId: string | null
  onRequestAAI: (
    questionId: string,
    branchId: string,
    context?: string,
  ) => Promise<void>
  onRequestRename: (branchId: string) => void | Promise<void>
  onRequestTerminate: (branchId: string) => void | Promise<void>
  onRequestPrune: (branchId: string) => void
  onSaveDraft: (
    branchId: string,
    questionId: string,
    payload: {
      draftText: string
      draftStatus: AnswerStatus
      selectedOptions?: string[]
      otherText?: string
    },
  ) => Promise<void>
  problemOutput: ProblemOutput | null
  onRevise: (revision: string) => Promise<void>
  // Per-question AAI dismiss (clears the slot in the conversation store).
  // Loading / suggestion state is read directly by each QuestionBlock
  // from `conversationStore.aaiByQuestion[questionId]`, so parallel
  // Assist requests never cancel each other.
  onDismissAAI: (questionId: string) => void
  // Branch tabs (above answer panel)
  branches: Array<{
    _id: string
    name?: string
    thematicName?: string
    userRenamedName?: string
    status: string
    depth: number
  }>
  onSelectBranch: (id: string) => void
  // Version history + continue-depth
  problemHistory: Array<{ version: number; timestamp: number; output: ProblemOutput }>
  onRestoreVersion: (version: number) => Promise<void>
  onContinueDepth: (query: string) => Promise<void>
  // Cleaner state for vagueness follow-up
  cleanerDiff: DiffChange[]
  vaguenessIssues: Array<{
    segment: string
    issue: string
    clarification_needed: string
  }>
  requiresClarification: boolean
  onResubmitWithClarifications: (clarifiedSituation: string) => void
  cleanerRevision: number
}

const StateRenderer = (props: StateRendererProps) => {
  const {
    state,
    isStreaming,
    streamContent,
    rawSituation,
    cleanedSituation,
    inputValue,
    onInput,
    onSubmitSituation,
    onConfirmClean,
    onGenerate,
    onSubmitRound,
    currentQuestions,
    activeBranchId,
    onRequestAAI,
    onRequestRename,
    onRequestTerminate,
    onRequestPrune,
    onSaveDraft,
    problemOutput,
    onRevise,
    onDismissAAI,
    branches,
    onSelectBranch,
    problemHistory,
    onRestoreVersion,
    onContinueDepth,
    cleanerDiff,
    vaguenessIssues,
    requiresClarification,
    onResubmitWithClarifications,
    cleanerRevision,
  } = props

  if (state === 'IDLE') {
    const wordCount = countWords(inputValue)
    const overLimit = wordCount > WORD_LIMIT
    return (
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <AgentAvatar tone="mint" icon={<Sparkles className="w-5 h-5 text-white" />} />
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
              Describe Your Situation
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Share the facts and observations about your design context. Focus
              on what you see and know — avoid jumping to solutions or
              conclusions.
            </p>
            <textarea
              className="textarea-field min-h-[150px]"
              placeholder="Example: In Bangalore's IT corridor, employees spend an average of 2-3 hours commuting daily..."
              value={inputValue}
              onChange={(e) => onInput(e.target.value)}
            />
            <div className="flex items-center justify-between mt-2">
              <span
                className={`text-xs ${
                  overLimit
                    ? 'text-red-500 font-medium'
                    : wordCount > WORD_LIMIT * 0.8
                    ? 'text-amber-500'
                    : 'text-slate-400'
                }`}
              >
                {wordCount} / {WORD_LIMIT} words
                {overLimit && ' — over limit'}
              </span>
            </div>
            <div className="flex justify-end mt-2">
              <Button
                rightIcon={<Send className="w-4 h-4" />}
                onClick={onSubmitSituation}
                disabled={!inputValue.trim() || isStreaming || overLimit}
                isLoading={isStreaming}
              >
                Submit Situation
              </Button>
            </div>
          </div>
        </div>
      </Card>
    )
  }

  if (state === 'CLEANING') {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <AgentAvatar tone="cleaner" icon={<Sparkles className="w-5 h-5 text-white" />} />
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">
              Cleaning in process…
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Removing inadvertent solutions and checking for vagueness.
              The cleaned text will appear once it&apos;s ready.
            </p>
          </div>
        </div>
        <div className="mt-6">
          <Loader variant="dots" />
        </div>
      </Card>
    )
  }

  if (state === 'CONFIRM_CLEAN') {
    return (
      <ConfirmCleanPanel
        rawSituation={rawSituation}
        cleanedSituation={cleanedSituation}
        cleanerDiff={cleanerDiff}
        vaguenessIssues={vaguenessIssues}
        requiresClarification={requiresClarification}
        onConfirmClean={onConfirmClean}
        onResubmitWithClarifications={onResubmitWithClarifications}
        revision={cleanerRevision}
      />
    )
  }

  if (state === 'QUESTIONING') {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <AgentAvatar tone="qai" icon={<HelpCircle className="w-5 h-5 text-white" />} />
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">
              QAI is generating questions…
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Creating curiosity-driven inquiry branches
            </p>
          </div>
        </div>
        <div className="mt-6">
          <Loader variant="dots" text="Formulating questions..." />
        </div>
      </Card>
    )
  }

  if (state === 'ANSWERING' || state === 'AAI_ASSIST') {
    if (!activeBranchId) {
      return (
        <Card className="p-6 text-center text-slate-600 dark:text-slate-400">
          Waiting for an active branch…
        </Card>
      )
    }
    return (
      <div className="space-y-3">
        <BranchTabs
          branches={branches}
          activeBranchId={activeBranchId}
          onBranchSelect={onSelectBranch}
          onRequestRename={onRequestRename}
          onRequestTerminate={onRequestTerminate}
          onRequestPrune={onRequestPrune}
        />
        <AnswerPanel
          questions={currentQuestions}
          branchId={activeBranchId}
          onRequestAAI={onRequestAAI}
          isStreaming={isStreaming}
          onDismissAAI={onDismissAAI}
          onSaveDraft={onSaveDraft}
        />
        <RoundSubmitBar onSubmit={onSubmitRound} />
      </div>
    )
  }

  if (state === 'EVALUATING') {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <AgentAvatar tone="system" icon={<MessageSquareQuote className="w-5 h-5 text-white" />} />
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">
              Evaluating answers…
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Checking confidence and first-principle signals
            </p>
          </div>
        </div>
        <div className="mt-6">
          <Loader variant="dots" />
        </div>
      </Card>
    )
  }

  if (state === 'READY_TO_GENERATE') {
    return (
      <Card className="p-6 text-center">
        <Lightbulb className="w-10 h-10 mx-auto text-amber-500 mb-3" />
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
          Ready to synthesize the problem
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          Enough signal has been collected. Generate the structured problem
          statement.
        </p>
        <Button onClick={onGenerate} leftIcon={<Play className="w-4 h-4" />}>
          Generate Problem
        </Button>
      </Card>
    )
  }

  if (state === 'GENERATING' || state === 'REVISING') {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-4 mb-4">
          <AgentAvatar tone="oai" icon={<Lightbulb className="w-5 h-5 text-white" />} />
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">
              {state === 'REVISING'
                ? 'Revising problem…'
                : 'Observer AI is synthesizing…'}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Assembling AI3C, cause-effect chains, and problem statements
            </p>
          </div>
        </div>
        <StreamPreview content={streamContent} />
      </Card>
    )
  }

  if (state === 'REVIEWING' || state === 'COMPLETED') {
    return (
      <ProblemReview
        problemOutput={problemOutput}
        onRevise={onRevise}
        isStreaming={isStreaming}
        history={problemHistory}
        onRestoreVersion={onRestoreVersion}
        onContinueDepth={onContinueDepth}
      />
    )
  }

  if (state === 'ERROR') {
    return (
      <Card className="p-6 text-center">
        <AlertTriangle className="w-10 h-10 mx-auto text-red-500 mb-3" />
        <p className="text-slate-700 dark:text-slate-200">
          Something went wrong. Use <code>/retry</code> to try again.
        </p>
      </Card>
    )
  }

  return null
}

// ---------- Confirm clean panel ---------- //

interface ConfirmCleanPanelProps {
  rawSituation: string
  cleanedSituation: string
  cleanerDiff: DiffChange[]
  vaguenessIssues: Array<{
    segment: string
    issue: string
    clarification_needed: string
  }>
  requiresClarification: boolean
  onConfirmClean: (useOriginal: boolean) => void
  onResubmitWithClarifications: (situation: string) => void
  revision: number
}

const SOFT_REVISION_LIMIT = 5

const ConfirmCleanPanel = ({
  rawSituation,
  cleanedSituation,
  cleanerDiff,
  vaguenessIssues,
  requiresClarification,
  onConfirmClean,
  onResubmitWithClarifications,
  revision,
}: ConfirmCleanPanelProps) => {
  const [clarifications, setClarifications] = useState<Record<number, string>>({})
  const [skipped, setSkipped] = useState<Record<number, boolean>>({})
  const [showDiff, setShowDiff] = useState(false)

  // Reset answers/skips when a new revision arrives.
  useEffect(() => {
    setClarifications({})
    setSkipped({})
  }, [revision, vaguenessIssues])

  const isResolved = (i: number): boolean =>
    skipped[i] || (clarifications[i] ?? '').trim().length > 0

  // Accept & Continue is blocked only while there's at least one
  // unanswered + unskipped clarification. The Cleaner's
  // requiresClarification flag still hard-blocks until at least one
  // question is answered/skipped.
  const allResolved =
    vaguenessIssues.length === 0 ||
    vaguenessIssues.every((_, i) => isResolved(i))
  const someAnswered = vaguenessIssues.some(
    (_, i) => (clarifications[i] ?? '').trim().length > 0,
  )

  const blocked =
    !allResolved || (requiresClarification && !someAnswered)

  // Re-clean requires every question in this revision to be either
  // answered or explicitly skipped, AND at least one to be answered
  // (otherwise we'd just resend the same situation).
  const everyQuestionResolved =
    vaguenessIssues.length === 0 ||
    vaguenessIssues.every(
      (_, i) => skipped[i] || (clarifications[i] ?? '').trim().length > 0,
    )
  const hasAtLeastOneAnswer = vaguenessIssues.some(
    (_, i) => !skipped[i] && (clarifications[i] ?? '').trim().length > 0,
  )
  const canResubmit = everyQuestionResolved && hasAtLeastOneAnswer

  const handleResubmit = () => {
    const additions = vaguenessIssues
      .map((vi, i) => {
        if (skipped[i]) return null
        const ans = (clarifications[i] ?? '').trim()
        if (!ans) return null
        return `Clarification — "${vi.segment}": ${ans}`
      })
      .filter(Boolean)
      .join('\n')

    const next = `${cleanedSituation || rawSituation}\n\n${additions}`
    onResubmitWithClarifications(next)
  }

  return (
    <Card className="p-6">
      <div className="flex items-start gap-4">
        <AgentAvatar tone="cleaner" icon={<Sparkles className="w-5 h-5 text-white" />} />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h3 className="font-semibold text-slate-900 dark:text-white">
              Review Changes
            </h3>
            <Badge
              variant={revision > 1 ? 'yellow' : 'mint'}
              size="sm"
            >
              Revision {revision}
            </Badge>
            {revision >= SOFT_REVISION_LIMIT && (
              <Badge variant="outline" size="sm" className="text-amber-600">
                Many revisions — consider accepting and moving on
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Cleaner AI has processed your input. Review the cleaned text and
            clarify or skip any vague segments before continuing.
          </p>

          <div className="p-4 rounded-xl bg-frosted-mint/20 dark:bg-dark-muted-teal/10 border border-slate-200 dark:border-slate-700 mb-4">
            <Markdown className="text-sm text-slate-700 dark:text-slate-300">
              {cleanedSituation || rawSituation}
            </Markdown>
          </div>

          {cleanerDiff.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setShowDiff((v) => !v)}
                className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 underline"
              >
                {showDiff ? 'Hide' : 'Show'} {cleanerDiff.length} change(s)
              </button>
              {showDiff && (
                <ul className="mt-2 space-y-2">
                  {cleanerDiff.map((d, i) => (
                    <li
                      key={i}
                      className="text-xs p-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                    >
                      <Badge variant="outline" size="sm" className="mr-2">
                        {d.type}
                      </Badge>
                      <span className="text-red-600 line-through">{d.original}</span>
                      {' → '}
                      <span className="text-emerald-600">{d.modified}</span>
                      <div className="text-slate-500 mt-1 italic">
                        <Markdown inline>{d.reason}</Markdown>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {vaguenessIssues.length > 0 && (
            <div className="mb-4 p-4 rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50/50 dark:bg-amber-900/10">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                Some parts of your situation are too vague.
                Clarify or skip each item, then re-clean or accept.
              </p>
              <ul className="space-y-3">
                {vaguenessIssues.map((vi, i) => {
                  const isSkipped = !!skipped[i]
                  return (
                    <li
                      key={i}
                      className={`p-3 rounded-lg border ${
                        isSkipped
                          ? 'border-slate-200 bg-slate-50 dark:bg-slate-800/40 opacity-70'
                          : 'border-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm text-slate-800 dark:text-slate-100">
                            <span className="font-medium">"{vi.segment}"</span> —{' '}
                            <span className="text-slate-500">{vi.issue}</span>
                          </p>
                          <p className="text-xs text-slate-500 mt-1 italic">
                            {vi.clarification_needed}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setSkipped((prev) => ({ ...prev, [i]: !prev[i] }))
                          }
                          className={`flex-shrink-0 px-2 py-1 text-xs rounded hover:bg-slate-100 dark:hover:bg-slate-800 ${
                            isSkipped
                              ? 'font-semibold text-amber-600'
                              : 'text-slate-500'
                          }`}
                        >
                          {isSkipped ? 'Undo skip' : 'Skip this'}
                        </button>
                      </div>
                      <textarea
                        className="textarea-field min-h-[60px] w-full mt-2"
                        placeholder={
                          isSkipped
                            ? 'Skipped — this segment will remain as-is.'
                            : 'Your clarification…'
                        }
                        value={clarifications[i] ?? ''}
                        disabled={isSkipped}
                        onChange={(e) =>
                          setClarifications((prev) => ({ ...prev, [i]: e.target.value }))
                        }
                      />
                    </li>
                  )
                })}
              </ul>
              <div className="flex justify-end mt-3">
                <Button
                  size="sm"
                  onClick={handleResubmit}
                  disabled={!canResubmit}
                  title={
                    canResubmit
                      ? 'Re-run Cleaner with the clarifications above'
                      : !everyQuestionResolved
                      ? 'Answer or skip every item before re-cleaning'
                      : 'Add at least one clarification (all-skipped won\'t change anything)'
                  }
                >
                  Re-clean with clarifications (Revision {revision + 1})
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => onConfirmClean(true)} disabled={blocked}>
              Use Original
            </Button>
            <Button onClick={() => onConfirmClean(false)} disabled={blocked}>
              {blocked
                ? 'Clarify or skip the items above'
                : 'Accept & Continue'}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}

// ---------- Message history ---------- //

const MessageHistory = ({ messages }: { messages: Message[] }) => {
  // Filter out raw aai_suggestion rows — the AAISuggestion component
  // renders the live suggestion inline, and once an answer is submitted
  // the human/AAI bubble shows the final text. Keep these rows in
  // Convex for export/audit, just don't render them in chat.
  const visible = useMemo(
    () => messages.filter((m) => m.role !== 'aai_suggestion'),
    [messages],
  )

  // Lookup of qai_question messages so a human_answer bubble can show
  // the original options with the user's selection highlighted (Q4).
  // Indexed by `_id` (parentMessageId match) AND by `round:orderInRound`
  // as a fallback for older rows that didn't persist parentMessageId.
  const questionLookup = useMemo(() => {
    const byId = new Map<string, Message>()
    const byRoundOrder = new Map<string, Message>()
    for (const m of messages) {
      if (m.role !== 'qai_question') continue
      byId.set(m._id, m)
      byRoundOrder.set(`${m.round}:${m.orderInRound}`, m)
    }
    const find = (answer: Message): Message | undefined => {
      if (answer.parentMessageId) {
        const hit = byId.get(answer.parentMessageId)
        if (hit) return hit
      }
      return byRoundOrder.get(`${answer.round}:${answer.orderInRound}`)
    }
    return { find }
  }, [messages])

  if (!visible.length) return null

  // Group consecutive messages by round so we can drop a sticky
  // "Depth (Round) N" divider between depths.
  const groups: Array<{ round: number; items: Message[] }> = []
  for (const m of visible) {
    const last = groups[groups.length - 1]
    if (!last || last.round !== m.round) {
      groups.push({ round: m.round, items: [m] })
    } else {
      last.items.push(m)
    }
  }

  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <div key={`round-${g.round}-${g.items[0]?._id ?? ''}`} className="space-y-4">
          <DepthDivider round={g.round} />
          {g.items.map((m) => (
            <MessageRow
              key={m._id}
              message={m}
              parentQuestion={
                m.role === 'human_answer' ? questionLookup.find(m) : undefined
              }
            />
          ))}
        </div>
      ))}
    </div>
  )
}

const DepthDivider = ({ round }: { round: number }) => (
  <div
    className="sticky top-0 z-10 flex items-center gap-2 py-2 backdrop-blur bg-white/80 dark:bg-slate-900/80"
    data-depth-divider={round}
  >
    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
    <span className="text-xs font-medium px-3 py-1 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
      Depth (Round) {round}
    </span>
    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
  </div>
)

const roleTone: Record<Message['role'], 'human' | 'cleaner' | 'qai' | 'aai' | 'oai' | 'system'> = {
  human_input: 'human',
  cleaner_output: 'cleaner',
  qai_question: 'qai',
  human_answer: 'human',
  aai_suggestion: 'aai',
  oai_output: 'oai',
  oai_revision: 'oai',
  system: 'system',
}

/**
 * Pretty label for the chat bubble. For human_answer messages we
 * distinguish between fully-typed, AAI-derived, AAI-edited, and hybrid
 * answers (the per-Q persisted flags are set by the answer panel).
 */
const labelForMessage = (m: Message): string => {
  if (m.role !== 'human_answer') {
    return m.role.replace(/_/g, ' ')
  }
  if (m.aaiSuggestionAppended) return 'Human + AAI Answer'
  if (m.aaiSuggestionUsed && m.aaiSuggestionEdited) return 'AAI Answer (Edited)'
  if (m.aaiSuggestionUsed) return 'AAI Answer'
  return 'Human Answer'
}

const MessageRow = ({
  message,
  parentQuestion,
}: {
  message: Message
  parentQuestion?: Message
}) => {
  const tone = roleTone[message.role] ?? 'system'
  const isHuman = tone === 'human'

  // For human answers to MCQ/MSQ, render every option with the
  // user's picks highlighted (Q4). We need the parent question's
  // options to know what was on offer. `selectedOptions` may store
  // the option id OR its text — match against either.
  const selectedOptionsView = useMemo(() => {
    if (message.role !== 'human_answer') return null
    const selected = message.selectedOptions ?? []
    const otherText = (message.otherText ?? '').trim()
    if (selected.length === 0 && !otherText) return null

    const rawOptions = (parentQuestion?.questionOptions ?? []) as Array<
      string | { id?: string; text?: string }
    >
    const options =
      rawOptions.length > 0
        ? rawOptions.map((o, i) =>
            typeof o === 'string'
              ? { id: o, text: o }
              : { id: o.id ?? o.text ?? String(i), text: o.text ?? o.id ?? '' },
          )
        : selected.map((s) => ({ id: s, text: s }))

    const isSelected = (id: string, text: string) =>
      selected.includes(id) || selected.includes(text)

    return { options, otherText, isSelected }
  }, [message, parentQuestion])

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex gap-3 ${isHuman ? 'flex-row-reverse' : ''}`}
      data-message-id={message._id}
      data-message-role={message.role}
      data-round={message.round}
      {...(message.role === 'qai_question'
        ? { 'data-question-id': message._id }
        : {})}
    >
      <AgentAvatar tone={tone} icon={isHuman ? <UserIcon className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />} small />
      <div className={`flex-1 max-w-[85%] ${isHuman ? 'text-right' : ''}`}>
        <div className={`flex items-center gap-2 mb-1 ${isHuman ? 'justify-end' : ''}`}>
          <Badge size="sm" variant="outline" className="text-xs capitalize">
            {labelForMessage(message)}
          </Badge>
          <span className="text-xs text-slate-400">
            {formatRelativeTime(message.createdAt)}
          </span>
        </div>
        <div
          className={`rounded-2xl p-3 inline-block text-left ${
            isHuman
              ? 'bg-slate-100 dark:bg-slate-800'
              : 'bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700'
          }`}
        >
          {message.content && message.content.trim().length > 0 && (
            <Markdown className="text-sm text-slate-800 dark:text-slate-100">
              {message.content}
            </Markdown>
          )}
          {message.role === 'qai_question' && message.questionOptions && message.questionOptions.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-400">
              {(message.questionOptions as unknown as Array<string | { text: string }>).map(
                (opt, i) => (
                  <li key={i}>
                    • {typeof opt === 'string' ? opt : opt.text}
                  </li>
                ),
              )}
            </ul>
          )}
          {selectedOptionsView && (
            <div className="mt-2 flex flex-wrap gap-1.5 text-left">
              {selectedOptionsView.options.map((opt) => {
                const picked = selectedOptionsView.isSelected(opt.id, opt.text)
                return (
                  <span
                    key={opt.id}
                    className={cn(
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs',
                      picked
                        ? 'border-light-icy-blue bg-light-icy-blue/20 text-slate-900 dark:border-dark-muted-teal dark:bg-dark-muted-teal/20 dark:text-white font-medium'
                        : 'border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 line-through opacity-60',
                    )}
                  >
                    {picked && <Check className="w-3 h-3" />}
                    {opt.text}
                  </span>
                )
              })}
              {selectedOptionsView.otherText && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs border-light-icy-blue bg-light-icy-blue/20 text-slate-900 dark:border-dark-muted-teal dark:bg-dark-muted-teal/20 dark:text-white font-medium">
                  <Check className="w-3 h-3" />
                  Other: {selectedOptionsView.otherText}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

// ---------- Answer panel ---------- //

interface AnswerPanelProps {
  questions: Question[]
  branchId: string
  onRequestAAI: (questionId: string, branchId: string, context?: string) => Promise<void>
  isStreaming: boolean
  /**
   * Clears the AAI slot for a single question (after Accept/Append or
   * when the user dismisses the suggestion).
   */
  onDismissAAI: (questionId: string) => void
  onSaveDraft: (
    branchId: string,
    questionId: string,
    payload: {
      draftText: string
      draftStatus: AnswerStatus
      selectedOptions?: string[]
      otherText?: string
    },
  ) => Promise<void>
}

/**
 * AnswerPanel (Round model v2)
 *
 * - Reads each draft from the global conversation store
 *   (`getAnswersForBranch`), so the round-level submit bar can compute
 *   readiness across every active branch.
 * - Writes every change into the store and debounces a backend
 *   `answerDrafts` upsert so the input survives refreshes and other tabs.
 * - There is intentionally no per-branch submit button — the global
 *   `RoundSubmitBar` advances all branches together.
 */
const AnswerPanel = ({
  questions,
  branchId,
  onRequestAAI,
  isStreaming,
  onDismissAAI,
  onSaveDraft,
}: AnswerPanelProps) => {
  const setAnswer = useConversationStore((s) => s.setAnswerForQuestion)
  const branchAnswers = useConversationStore(
    (s) => s.answersPerBranch.get(branchId),
  )

  // Per-question debounce timers used to throttle backend draft autosave.
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const scheduleSave = (qid: string, draft: Answer) => {
    const existing = saveTimers.current[qid]
    if (existing) clearTimeout(existing)
    saveTimers.current[qid] = setTimeout(() => {
      void onSaveDraft(branchId, qid, {
        draftText: draft.content ?? '',
        draftStatus: draft.status,
        selectedOptions: draft.selectedOptions,
        otherText: draft.otherText,
      })
    }, 600)
  }

  useEffect(() => {
    return () => {
      for (const t of Object.values(saveTimers.current)) clearTimeout(t)
    }
  }, [])

  const setDraft = (qid: string, patch: Partial<Answer>) => {
    const existing: Answer = (branchAnswers?.get(qid) as Answer | undefined) ?? {
      questionId: qid,
      status: 'answered' as AnswerStatus,
    }
    // If the user types into the textarea after having clicked
    // "Use as Answer", the AAI text is no longer verbatim — flip the
    // `edited` flag so the chat bubble label becomes "AAI Answer
    // (Edited)". Don't touch `aaiSuggestionAppended` — once an answer
    // is hybrid, it stays hybrid regardless of further typing.
    const isContentEdit =
      typeof patch.content === 'string' && patch.content !== existing.content
    const next: Answer = {
      ...existing,
      ...patch,
      questionId: qid,
      ...(isContentEdit && existing.aaiSuggestionUsed
        ? { aaiSuggestionEdited: true }
        : {}),
    }
    setAnswer(branchId, qid, next)
    scheduleSave(qid, next)
  }

  // "Use as Answer" — replace the textarea with the AAI suggestion verbatim.
  // Resets the appended flag because this is the canonical AAI path, and
  // sets edited=false; subsequent textarea edits will flip edited=true.
  const setAnswerContent = (qid: string, text: string) => {
    const existing: Answer = (branchAnswers?.get(qid) as Answer | undefined) ?? {
      questionId: qid,
      status: 'answered' as AnswerStatus,
    }
    const next: Answer = {
      ...existing,
      questionId: qid,
      status: 'answered',
      content: text,
      aaiSuggestionUsed: true,
      aaiSuggestionEdited: false,
      aaiSuggestionAppended: false,
    }
    setAnswer(branchId, qid, next)
    scheduleSave(qid, next)
  }

  // "Add to Answer" — append the AAI suggestion to whatever the human
  // already typed. Marks the answer as a hybrid so the chat bubble shows
  // "Human + AAI Answer".
  const appendToAnswer = (qid: string, text: string) => {
    const existing: Answer = (branchAnswers?.get(qid) as Answer | undefined) ?? {
      questionId: qid,
      status: 'answered' as AnswerStatus,
    }
    const prevContent = existing.content ?? ''
    const merged = prevContent ? `${prevContent}\n\n${text}` : text
    const next: Answer = {
      ...existing,
      questionId: qid,
      status: 'answered',
      content: merged,
      aaiSuggestionAppended: true,
      aaiSuggestionUsed: false,
      aaiSuggestionEdited: false,
    }
    setAnswer(branchId, qid, next)
    scheduleSave(qid, next)
  }

  if (!questions.length) {
    return (
      <Card className="p-6 text-center text-slate-600 dark:text-slate-400">
        Waiting for questions…
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {questions.map((q, idx) => {
        const draft = branchAnswers?.get(q.id) as Answer | undefined
        return (
          <QuestionBlock
            key={q.id}
            index={idx + 1}
            question={q}
            draft={draft}
            onChange={(patch) => setDraft(q.id, patch)}
            onAAI={() => onRequestAAI(q.id, branchId)}
            onAcceptAAI={(text) => {
              setAnswerContent(q.id, text)
              onDismissAAI(q.id)
            }}
            onAppendAAI={(text) => {
              appendToAnswer(q.id, text)
              onDismissAAI(q.id)
            }}
            onDismissAAI={() => onDismissAAI(q.id)}
          />
        )
      })}

      {isStreaming && (
        <div className="text-xs text-slate-500 dark:text-slate-400 italic text-center">
          Round in flight — please wait…
        </div>
      )}
    </div>
  )
}

const QuestionBlock = ({
  index,
  question,
  draft,
  onChange,
  onAAI,
  onAcceptAAI,
  onAppendAAI,
  onDismissAAI,
}: {
  index: number
  question: Question
  draft?: Answer
  onChange: (patch: Partial<Answer>) => void
  onAAI: () => void
  onAcceptAAI: (text: string) => void
  onAppendAAI: (text: string) => void
  onDismissAAI: () => void
}) => {
  // Each QuestionBlock subscribes to its OWN AAI slot so multiple
  // Assist requests can stream in parallel without interfering. The
  // selector is keyed by `question.id`, so a chunk arriving for any
  // other question won't re-render this block.
  const aaiSlot = useConversationStore(
    (s) => s.aaiByQuestion[question.id],
  )
  const aaiActive = !!aaiSlot?.isActive
  const aaiWebSearchInProgress = !!aaiSlot?.webSearchInProgress
  const aaiSuggestion = aaiSlot?.suggestion ?? null
  const aaiStreamingContent = aaiSlot?.streamingContent ?? ''

  const status = draft?.status ?? 'answered'
  const isSkipped = status === 'skipped'

  const content = draft?.content ?? ''
  const wordCount = countWords(content)
  const overLimit = wordCount > WORD_LIMIT

  return (
    <Card className={`p-5 ${isSkipped ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3 mb-3">
        <AgentAvatar tone="qai" icon={<HelpCircle className="w-4 h-4 text-white" />} small />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="blue" size="sm">
              Q{index}
            </Badge>
            <Badge variant="outline" size="sm" className="uppercase">
              {question.type}
            </Badge>
            {question.ai3cTarget && (
              <Badge variant="mint" size="sm">
                {question.ai3cTarget}
              </Badge>
            )}
            {isSkipped && (
              <Badge variant="outline" size="sm" className="text-amber-600">
                Skipped
              </Badge>
            )}
          </div>
          <p className="text-slate-800 dark:text-slate-100 font-medium">
            {question.text}
          </p>
          {question.intent && (
            <p className="text-xs text-slate-500 mt-1 italic">
              Intent: {question.intent}
            </p>
          )}
        </div>
      </div>

      {question.type === 'open' && (
        <>
          <textarea
            className="textarea-field min-h-[80px] w-full"
            placeholder="Your answer..."
            value={content}
            disabled={isSkipped}
            onChange={(e) =>
              onChange({ content: e.target.value, status: 'answered' })
            }
          />
          <div
            className={`text-xs mt-1 ${
              overLimit
                ? 'text-red-500 font-medium'
                : wordCount > WORD_LIMIT * 0.8
                ? 'text-amber-500'
                : 'text-slate-400'
            }`}
          >
            {wordCount} / {WORD_LIMIT} words
            {overLimit && ' — over limit'}
          </div>
        </>
      )}

      {(question.type === 'mcq' || question.type === 'msq') && question.options && (
        <div className={`space-y-2 ${isSkipped ? 'pointer-events-none' : ''}`}>
          {(question.options as unknown as Array<string | { id: string; text: string }>).map((opt, i) => {
            const optId = typeof opt === 'string' ? `opt_${i}` : opt.id
            const optText = typeof opt === 'string' ? opt : opt.text
            const isMulti = question.type === 'msq'
            const selected = draft?.selectedOptions ?? []
            const checked = selected.includes(optId)
            return (
              <label
                key={optId}
                className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
              >
                <input
                  type={isMulti ? 'checkbox' : 'radio'}
                  name={`q_${question.id}`}
                  checked={checked}
                  onChange={() => {
                    let next: string[]
                    if (isMulti) {
                      next = checked
                        ? selected.filter((x) => x !== optId)
                        : [...selected, optId]
                    } else {
                      next = [optId]
                    }
                    onChange({ selectedOptions: next, status: 'answered' })
                  }}
                />
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  {optText}
                </span>
              </label>
            )
          })}
        </div>
      )}

      <div className="flex items-center justify-between mt-3">
        <button
          onClick={() => {
            if (isSkipped) {
              onChange({ status: 'answered' })
            } else {
              onChange({ status: 'skipped', content: undefined, selectedOptions: undefined })
            }
          }}
          className={`px-3 py-1 text-xs rounded hover:bg-slate-100 dark:hover:bg-slate-800 ${
            isSkipped ? 'font-semibold text-amber-600' : 'text-slate-500'
          }`}
        >
          {isSkipped ? 'Undo skip' : 'Skip this question'}
        </button>
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Bot className="w-4 h-4" />}
          onClick={onAAI}
          disabled={isSkipped}
          isLoading={aaiActive}
        >
          {aaiActive ? 'Thinking…' : 'Assist (AAI)'}
        </Button>
      </div>

      {/* AAI inline suggestion — driven by THIS question's slot only,
          so several blocks can render their own loading / suggestion
          independently and at the same time. */}
      {aaiActive && !aaiSuggestion && (
        <div className="mt-4 p-3 rounded-lg border border-rose-200 dark:border-rose-800/40 bg-rose-50/40 dark:bg-rose-900/10">
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-rose-500 animate-pulse" />
            <span className="text-sm text-rose-700 dark:text-rose-300">
              {aaiWebSearchInProgress
                ? 'AAI is searching the web…'
                : aaiStreamingContent
                ? 'AAI is drafting an answer…'
                : 'AAI is thinking…'}
            </span>
          </div>
        </div>
      )}

      {aaiSuggestion && (
        <div className="mt-4">
          <AAISuggestion
            suggestion={aaiSuggestion}
            onAccept={onAcceptAAI}
            onAppend={onAppendAAI}
            onDismiss={onDismissAAI}
          />
        </div>
      )}
    </Card>
  )
}

// ---------- Problem review ---------- //

interface ProblemReviewProps {
  problemOutput: ProblemOutput | null
  onRevise: (revision: string) => Promise<void>
  isStreaming: boolean
  history: Array<{ version: number; timestamp: number; output: ProblemOutput }>
  onRestoreVersion: (version: number) => Promise<void>
  onContinueDepth: (query: string) => Promise<void>
}

const ProblemReview = ({
  problemOutput,
  onRevise,
  isStreaming,
  history,
  onRestoreVersion,
  onContinueDepth,
}: ProblemReviewProps) => {
  const [revision, setRevision] = useState('')
  const [continueQuery, setContinueQuery] = useState('')
  const [selectedVersion, setSelectedVersion] = useState<number | 'current'>('current')

  if (!problemOutput) {
    return (
      <Card className="p-6 text-center text-slate-600 dark:text-slate-400">
        Problem output will appear here once generation completes.
      </Card>
    )
  }

  // Build a [current, ...history (descending by version)] list.
  const currentVersion =
    problemOutput.version ?? problemOutput.metadata?.version ?? 1
  const sortedHistory = [...history].sort((a, b) => b.version - a.version)

  const displayed: ProblemOutput =
    selectedVersion === 'current'
      ? problemOutput
      : (sortedHistory.find((h) => h.version === selectedVersion)?.output as ProblemOutput) ??
        problemOutput
  const isViewingHistorical = selectedVersion !== 'current'

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          <h2 className="font-semibold text-slate-900 dark:text-white">
            Synthesized Problem
          </h2>
          {(displayed.version || displayed.metadata?.version) && (
            <Badge variant="outline" size="sm">
              v{displayed.version ?? displayed.metadata?.version}
              {isViewingHistorical && ' (historical)'}
            </Badge>
          )}
          {(sortedHistory.length > 0 || true) && (
            <div className="ml-auto flex items-center gap-2">
              <label className="text-xs text-slate-500">Version:</label>
              <select
                value={String(selectedVersion)}
                aria-label="Problem output version"
                title="Problem output version"
                onChange={(e) => {
                  const v = e.target.value
                  setSelectedVersion(v === 'current' ? 'current' : Number(v))
                }}
                className="text-sm border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 bg-white dark:bg-slate-800"
              >
                <option value="current">v{currentVersion} (current)</option>
                {sortedHistory.map((h) => (
                  <option key={h.version} value={String(h.version)}>
                    v{h.version}
                  </option>
                ))}
              </select>
              {isViewingHistorical && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={async () => {
                    await onRestoreVersion(selectedVersion as number)
                    setSelectedVersion('current')
                    toast.success(`Restored v${selectedVersion}`)
                  }}
                >
                  Restore this version
                </Button>
              )}
            </div>
          )}
        </div>

        <Section title="Current Undesirable Situation">
          <Markdown className="text-sm text-slate-700 dark:text-slate-200">
            {displayed.currentUndesirableSituation}
          </Markdown>
        </Section>

        <Section title="Future Desired Situation">
          <Markdown className="text-sm text-slate-700 dark:text-slate-200">
            {displayed.futureDesiredSituation}
          </Markdown>
        </Section>

        <Section title="AI3C">
          <ul className="text-sm text-slate-700 dark:text-slate-200 space-y-1">
            <li><b>Activity:</b> {displayed.ai3c.activity}</li>
            <li><b>Item:</b> {displayed.ai3c.item}</li>
            <li><b>Contradiction:</b> {displayed.ai3c.contradiction}</li>
            <li><b>Constraints:</b> {displayed.ai3c.constraints.join('; ') || '—'}</li>
            <li><b>Criteria:</b> {displayed.ai3c.criteria.join('; ') || '—'}</li>
          </ul>
        </Section>

        {displayed.problemStatements && displayed.problemStatements.length > 0 && (
          <Section title="Problem Statements">
            <ol className="text-sm text-slate-700 dark:text-slate-200 space-y-2 list-decimal pl-5">
              {displayed.problemStatements.map((ps: ProblemOutput['problemStatements'][number], i: number) => (
                <li key={i}>
                  <Markdown className="text-sm">{ps.statement}</Markdown>
                  {ps.designDirections && ps.designDirections.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Directions: {ps.designDirections.join('; ')}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          </Section>
        )}

        {displayed.narrativeSummary && (
          <Section title="Narrative">
            <Markdown className="text-sm text-slate-700 dark:text-slate-200">
              {displayed.narrativeSummary}
            </Markdown>
          </Section>
        )}
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
          Request a revision
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          OAI will incorporate your feedback into a new version (the previous
          version is preserved in the version dropdown above).
        </p>
        <textarea
          className="textarea-field min-h-[80px] w-full"
          placeholder="Describe the change you'd like (e.g. 'Make the criteria more quantitative')"
          value={revision}
          onChange={(e) => setRevision(e.target.value)}
          disabled={isViewingHistorical}
        />
        <div className="flex justify-end mt-3">
          <Button
            leftIcon={<RotateCcw className="w-4 h-4" />}
            onClick={async () => {
              if (!revision.trim()) return
              await onRevise(revision.trim())
              setRevision('')
            }}
            disabled={!revision.trim() || isStreaming || isViewingHistorical}
            isLoading={isStreaming}
          >
            Revise
          </Button>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
          Continue exploring with OAI
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          Ask OAI a follow-up question about the synthesis. Useful for
          digging into specific cause-effect chains, stakeholder concerns,
          or constraint trade-offs without rerunning the QAI tree.
        </p>
        <textarea
          className="textarea-field min-h-[80px] w-full"
          placeholder="e.g. 'Walk me through the strongest cause-effect chain in more detail'"
          value={continueQuery}
          onChange={(e) => setContinueQuery(e.target.value)}
        />
        <div className="flex justify-end mt-3">
          <Button
            variant="secondary"
            leftIcon={<Lightbulb className="w-4 h-4" />}
            onClick={async () => {
              if (!continueQuery.trim()) return
              await onContinueDepth(continueQuery.trim())
              setContinueQuery('')
            }}
            disabled={!continueQuery.trim() || isStreaming}
          >
            Ask OAI
          </Button>
        </div>
      </Card>
    </div>
  )
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mt-4">
    <h4 className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
      {title}
    </h4>
    {children}
  </div>
)

// ---------- Tree sidebar ---------- //

interface TreeSidebarBranch {
  _id: string
  name?: string
  thematicName?: string
  userRenamedName?: string
  status: string
  depth: number
  localDepth?: number
  spawnedAtRound?: number
}

interface BranchProgressSummary {
  spawnedAt: number
  localDepth: number
  curAnswered: number
  curTotal: number
  cumAnswered: number
  cumTotal: number
}

interface TreeSidebarProps {
  branches: TreeSidebarBranch[]
  activeBranchId: string | null
  onSelectBranch: (id: string) => void
  onRenameBranch: (id: string, name: string) => Promise<void>
  onTerminateBranch: (id: string, reason?: string) => Promise<void>
  onPruneBranch: (id: string) => Promise<void>
  ai3cCoverage?: {
    activity: number
    item: number
    contradiction: number
    constraint: number
    criteria: number
  }
  canGenerate: boolean
  /**
   * If `canGenerate` is false, this short string explains why so the
   * Generate button can show a richer tooltip (e.g. "A synthesis is
   * already running"). Falls back to the generic "answer all
   * questions first" tooltip when not provided.
   */
  generateDisabledReason?: string
  onGenerate: () => void
  currentRound: number
  messagesByBranch: Map<string, Message[]>
  /**
   * Per-branch in-progress draft answers for the current round, sourced
   * directly from the Zustand store. This is what makes the "Current Q&A"
   * counter tick up live as the user types/skips, before they hit
   * Submit Round.
   */
  draftsByBranch: Map<string, Map<string, Answer>>
  /**
   * Synthesis section (multi-card flow). Each row represents one
   * "Generate Problem Statement" run; the user can click between them
   * the same way they click between branches. The list is rendered
   * under a separate "Synthesis" header at the very bottom of the
   * sidebar.
   */
  syntheses: SynthesisRow[]
  activeSynthesisId: string | null
  onSelectSynthesis: (id: string) => void
  /**
   * Opens the project-wide ExportModal. Wired into the synthesis-card
   * kebab so each card has its own "Export as…" entry without
   * duplicating the modal instance.
   */
  onExportSynthesis: () => void
}

/**
 * Treat an answer (live draft OR persisted message) as "resolved" if it
 * has any non-empty content/options OR is explicitly marked skipped.
 * Mirrors `isAnswerResolved` in `conversationStore.ts` so the sidebar
 * counter agrees with the Submit-Round gate.
 */
function isResolvedAnswerLike(a: {
  content?: string
  selectedOptions?: string[]
  otherText?: string
  answerStatus?: string
  status?: string
}): boolean {
  const status = a.answerStatus ?? a.status
  if (status === 'skipped') return true
  const hasText = (a.content ?? '').trim().length > 0
  const hasOptions = (a.selectedOptions?.length ?? 0) > 0
  const hasOther = (a.otherText ?? '').trim().length > 0
  return hasText || hasOptions || hasOther
}

/**
 * For a single branch, derive both the cumulative answered/total counts
 * (across every depth this branch has explored) and the current round's
 * answered/total counts. "Answered" includes any resolved status —
 * answered, partial or skipped — per Q1c.
 *
 * Source-of-truth strategy:
 * - Past rounds (round != currentRound): both questions and answers come
 *   from persisted Convex `messages` (immutable history).
 * - Current round (round === currentRound): questions still come from
 *   `messages` (QAI persists them on emit), but answers come from the
 *   live Zustand draft map so the counter ticks up the moment the user
 *   types/skips/picks an option, BEFORE they Submit Round.
 */
function summarizeBranchProgress(
  branch: TreeSidebarBranch,
  messages: Message[] | undefined,
  currentRound: number,
  liveDrafts: Map<string, Answer> | undefined,
): BranchProgressSummary {
  const msgs = messages ?? []
  const questions = msgs.filter((m) => m.role === 'qai_question')
  const persistedAnswers = msgs.filter(
    (m) => m.role === 'human_answer' && isResolvedAnswerLike(m),
  )

  // The store's `currentRound` is bumped only on `round_complete`, so on
  // the very first round it can still be 0 while QAI has already
  // persisted round-1 questions. Use whichever is larger between the
  // store value and the highest qai_question round we've seen for this
  // branch — that's the round currently being answered.
  const maxQuestionRound = questions.reduce(
    (max, q) => Math.max(max, q.round ?? 0),
    0,
  )
  const effectiveRound = Math.max(currentRound, maxQuestionRound)

  // Past round contributions.
  const pastTotal = questions.filter((q) => q.round !== effectiveRound).length
  const pastAnswered = persistedAnswers.filter(
    (a) => a.round !== effectiveRound,
  ).length

  // Current round contributions.
  const currentQuestions = questions.filter((q) => q.round === effectiveRound)
  const curTotal = currentQuestions.length
  let curAnswered = 0
  if (curTotal > 0) {
    if (liveDrafts && liveDrafts.size > 0) {
      for (const q of currentQuestions) {
        const draft = liveDrafts.get(q._id)
        if (draft && isResolvedAnswerLike(draft)) curAnswered++
      }
    } else {
      // No live drafts (branch isn't open in this tab) — fall back to
      // any persisted human_answer that was already submitted for this
      // round. After a successful round_complete the store wipes drafts
      // and the messages table holds the final state.
      curAnswered = persistedAnswers.filter(
        (a) => a.round === effectiveRound,
      ).length
    }
  }

  return {
    spawnedAt: branch.spawnedAtRound ?? 0,
    localDepth: branch.localDepth ?? branch.depth,
    curAnswered,
    curTotal,
    cumAnswered: pastAnswered + curAnswered,
    cumTotal: pastTotal + curTotal,
  }
}

const TreeSidebar = ({
  branches,
  activeBranchId,
  onSelectBranch,
  onRenameBranch,
  onTerminateBranch,
  onPruneBranch,
  ai3cCoverage,
  canGenerate,
  generateDisabledReason,
  onGenerate,
  currentRound,
  messagesByBranch,
  draftsByBranch,
  syntheses,
  activeSynthesisId,
  onSelectSynthesis,
  onExportSynthesis,
}: TreeSidebarProps) => {
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-slate-200 dark:border-slate-700">
        <h2 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <TreeDeciduous className="w-5 h-5 text-light-icy-blue dark:text-dark-muted-teal" />
          Conversation Tree
        </h2>
      </div>

      <div className="p-4 overflow-y-auto flex-1">
        {ai3cCoverage && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              AI3C Coverage
            </h3>
            <AI3CCoverageProgress coverage={ai3cCoverage} />
          </div>
        )}

        <div>
          <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            Branches ({branches.length})
          </h3>
          <div className="space-y-2">
            {branches.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No branches yet
              </p>
            ) : (
              branches.map((b) => (
                <BranchSidebarItem
                  key={b._id}
                  branch={b}
                  isActive={activeBranchId === b._id && !activeSynthesisId}
                  onSelect={() => onSelectBranch(b._id)}
                  onRename={onRenameBranch}
                  onTerminate={onTerminateBranch}
                  onPrune={onPruneBranch}
                  currentRound={currentRound}
                  progress={summarizeBranchProgress(
                    b,
                    messagesByBranch.get(b._id),
                    currentRound,
                    draftsByBranch.get(b._id),
                  )}
                />
              ))
            )}
          </div>
        </div>

        {/* Synthesis section — one card per Generate run, persisted across
            reloads. Clicking a card swaps the main panel from chat history
            to the OAI synthesis output (with revise + continue chat). */}
        {syntheses.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-light-icy-blue dark:text-dark-muted-teal" />
              Synthesis ({syntheses.length})
            </h3>
            <div className="space-y-2">
              {syntheses.map((s) => (
                <SynthesisSidebarItem
                  key={s._id}
                  synthesis={s}
                  isActive={activeSynthesisId === s._id}
                  onSelect={() => onSelectSynthesis(s._id)}
                  onExport={onExportSynthesis}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/60">
        <Button
          className="w-full"
          onClick={onGenerate}
          disabled={!canGenerate}
          leftIcon={<Lightbulb className="w-4 h-4" />}
          title={
            canGenerate
              ? 'Generate the AI3C problem statement now'
              : generateDisabledReason ??
                'Answer or skip every question in every active branch first.'
          }
        >
          Generate problem statement
        </Button>
      </div>
    </div>
  )
}

const BranchSidebarItem = ({
  branch: b,
  isActive,
  onSelect,
  onRename,
  onTerminate,
  onPrune,
  currentRound,
  progress,
}: {
  branch: TreeSidebarBranch
  isActive: boolean
  onSelect: () => void
  onRename: (id: string, name: string) => Promise<void>
  onTerminate: (id: string, reason?: string) => Promise<void>
  onPrune: (id: string) => Promise<void>
  currentRound: number
  progress: BranchProgressSummary
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const label = b.userRenamedName || b.thematicName || b.name || `Branch ${b._id.slice(-6)}`

  const statusVariant: 'blue' | 'mint' | 'yellow' | 'default' =
    b.status === 'active'
      ? 'blue'
      : b.status === 'pruned'
      ? 'default'
      : b.status.startsWith('terminated')
      ? 'mint'
      : 'yellow'

  const currentRoundComplete =
    progress.curTotal > 0 && progress.curAnswered >= progress.curTotal

  const handleRename = async () => {
    setMenuOpen(false)
    const next = window.prompt('Rename branch to:', label)
    if (!next || !next.trim()) return
    try {
      await onRename(b._id, next.trim())
      toast.success('Branch renamed')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Rename failed')
    }
  }

  const handleTerminate = async () => {
    setMenuOpen(false)
    const reason = window.prompt('Termination reason (optional):', '') || undefined
    try {
      await onTerminate(b._id, reason)
      toast.success('Branch terminated')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Terminate failed')
    }
  }

  const handlePrune = async () => {
    setMenuOpen(false)
    // Workspace owns the cascade-choice modal; we just open it.
    try {
      await onPrune(b._id)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Prune failed')
    }
  }

  return (
    <div
      className={`relative rounded-lg border transition-colors ${
        isActive
          ? 'bg-light-icy-blue/10 dark:bg-dark-muted-teal/10 border-light-icy-blue dark:border-dark-muted-teal'
          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
      } ${b.status === 'pruned' ? 'opacity-60' : ''}`}
    >
      <button onClick={onSelect} className="w-full text-left p-3 pr-9">
        <div className="flex items-start gap-2">
          <span className="text-sm font-medium text-slate-900 dark:text-white flex-1">
            {label}
          </span>
          {currentRoundComplete && (
            <CheckCircle2
              className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5"
              aria-label="Current round complete"
            />
          )}
        </div>
        <div className="mt-1 grid grid-cols-3 gap-x-2 gap-y-0.5 text-[11px] text-slate-500 dark:text-slate-400">
          <span>
            Spawned <span className="font-medium">D{progress.spawnedAt}</span>
          </span>
          <span>
            Now <span className="font-medium">D{progress.localDepth}</span>
          </span>
          <span>
            Round <span className="font-medium">{currentRound}</span>
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-300">
          <span
            className={
              currentRoundComplete
                ? 'text-emerald-600 dark:text-emerald-400 font-medium'
                : ''
            }
          >
            Current Q&A {progress.curAnswered}/{progress.curTotal}
          </span>
          <span className="text-slate-400">·</span>
          <span>
            Total Q&A {progress.cumAnswered}/{progress.cumTotal}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          <Badge variant={statusVariant} size="sm">
            {b.status.replace(/_/g, ' ')}
          </Badge>
        </div>
      </button>

      <button
        type="button"
        aria-label="Branch actions"
        onClick={(e) => {
          e.stopPropagation()
          setMenuOpen((v) => !v)
        }}
        onBlur={() => setTimeout(() => setMenuOpen(false), 150)}
        className="absolute top-2 right-2 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>

      {menuOpen && (
        <div className="absolute z-20 right-2 top-9 w-44 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg overflow-hidden">
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onSelect()
              setMenuOpen(false)
            }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Set Active
          </button>
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleRename}
            className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Rename…
          </button>
          {b.status === 'active' && (
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleTerminate}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 text-amber-600"
            >
              Terminate
            </button>
          )}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={handlePrune}
            className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 text-red-600"
          >
            Prune…
          </button>
        </div>
      )}
    </div>
  )
}

// ---------- Synthesis sidebar item (Synthesis section, kebab → Export) ---- //

/**
 * One row in the sidebar's "Synthesis" section. Each card represents
 * a single OAI generation run; revisions update the same card in
 * place (so `revision` ticks 1 → 2 → 3 …) and the per-card chat lives
 * inside `SynthesisCardPanel` when the card is selected.
 *
 * Kebab menu intentionally exposes a single action — "Export as…" —
 * which opens the project-wide `ExportModal` (latex / markdown /
 * json / mermaid / graphviz). Rename / delete are still not offered
 * because synthesis identity is implicit (round + revision + ts).
 */
const SynthesisSidebarItem = ({
  synthesis: s,
  isActive,
  onSelect,
  onExport,
}: {
  synthesis: SynthesisRow
  isActive: boolean
  onSelect: () => void
  onExport: () => void
}) => {
  const [menuOpen, setMenuOpen] = useState(false)

  const ts = new Date(s.createdAt)
  const dateLabel = ts.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  const statusVariant: 'blue' | 'mint' | 'yellow' | 'red' =
    s.status === 'ready'
      ? 'mint'
      : s.status === 'failed'
      ? 'red'
      : s.status === 'revising'
      ? 'yellow'
      : 'blue'

  // Export only makes sense once the synthesis has produced output.
  const canExport = s.status === 'ready' && !!s.output

  return (
    <div
      className={cn(
        'relative rounded-lg border transition-colors cursor-pointer',
        isActive
          ? 'bg-light-icy-blue/10 dark:bg-dark-muted-teal/10 border-light-icy-blue dark:border-dark-muted-teal'
          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600',
      )}
    >
      <button onClick={onSelect} className="w-full text-left p-3 pr-9">
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-light-icy-blue dark:text-dark-muted-teal flex-shrink-0 mt-0.5" />
          <span className="text-sm font-medium text-slate-900 dark:text-white flex-1">
            Synthesis · Round {s.generatedAtRound}
            {s.revision > 1 && (
              <span className="text-slate-500 dark:text-slate-400 font-normal">
                {' '}(rev {s.revision})
              </span>
            )}
          </span>
        </div>
        <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 ml-6">
          {dateLabel}
          {s.migratedFromLegacy && (
            <span className="ml-2 italic opacity-75">imported</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1.5 ml-6">
          <Badge variant={statusVariant} size="sm">
            {s.status === 'generating'
              ? 'generating…'
              : s.status === 'revising'
              ? 'revising…'
              : s.status}
          </Badge>
          {(s.oaiChat?.length ?? 0) > 0 && (
            <span className="text-[10px] text-slate-400 dark:text-slate-500">
              {Math.floor((s.oaiChat?.length ?? 0) / 2)} chat
            </span>
          )}
        </div>
      </button>

      <button
        type="button"
        aria-label="Synthesis actions"
        onClick={(e) => {
          e.stopPropagation()
          setMenuOpen((v) => !v)
        }}
        onBlur={() => setTimeout(() => setMenuOpen(false), 150)}
        className="absolute top-2 right-2 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>

      {menuOpen && (
        <div className="absolute z-20 right-2 top-9 w-44 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg overflow-hidden">
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (!canExport) return
              onExport()
              setMenuOpen(false)
            }}
            disabled={!canExport}
            title={
              canExport
                ? 'Open the export modal (markdown, LaTeX, JSON, Mermaid, GraphViz)'
                : 'Export becomes available once this synthesis finishes generating'
            }
            className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            Export as…
          </button>
        </div>
      )}
    </div>
  )
}

// ---------- Synthesis main panel (replaces chat when a card is active) ---- //

/**
 * Full-screen view of one synthesis card, rendered in the main
 * conversation pane when its sidebar item is selected. Hosts:
 *   1. The OAI problem output (with per-card revision dropdown)
 *   2. "Request a revision" textarea (updates same card, rev++)
 *   3. "Continue exploring with OAI" — a chat thread persisted to
 *      `synthesis.oaiChat`
 *
 * The user can return to a branch by clicking that branch in the
 * sidebar; the parent component clears `activeSynthesisId` then.
 */
const SynthesisCardPanel = ({
  synthesis,
  isStreaming,
  streamContent,
  onBackToChat,
  onRevise,
  onContinueChat,
}: {
  synthesis: SynthesisRow
  isStreaming: boolean
  streamContent: string
  onBackToChat: () => void
  onRevise: (text: string) => Promise<void>
  onContinueChat: (text: string) => Promise<void>
}) => {
  const [revision, setRevision] = useState('')
  const [chatInput, setChatInput] = useState('')
  const [chatSubmitting, setChatSubmitting] = useState(false)
  const [selectedRevision, setSelectedRevision] = useState<number | 'current'>('current')

  const dateLabel = new Date(synthesis.createdAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  const isBusy = synthesis.status === 'generating' || synthesis.status === 'revising'

  // Per-card revision history (stored on the synthesis row itself).
  const sortedHistory = [...(synthesis.revisionHistory ?? [])].sort(
    (a, b) => b.revision - a.revision,
  )

  // The output to display: latest unless the user explicitly picks a
  // historical revision from the dropdown.
  const displayed: ProblemOutput | undefined =
    selectedRevision === 'current'
      ? synthesis.output
      : sortedHistory.find((h) => h.revision === selectedRevision)?.output ??
        synthesis.output
  const isViewingHistorical = selectedRevision !== 'current'

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="blue" size="sm">Synthesis</Badge>
          <span className="text-sm font-medium text-slate-900 dark:text-white">
            Round {synthesis.generatedAtRound}
          </span>
          <Badge variant="default" size="sm">rev {synthesis.revision}</Badge>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {dateLabel}
          </span>
        </div>
        <Button size="sm" variant="ghost" onClick={onBackToChat}
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          Back to chat
        </Button>
      </div>

      {/* While OAI is streaming the very first generation for this
          card, show a live preview. Once `synthesis.output` is set we
          switch to the formatted ProblemOutput card. */}
      {isBusy && !synthesis.output && (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <Loader2 className="w-5 h-5 animate-spin text-light-icy-blue dark:text-dark-muted-teal" />
            <h3 className="font-semibold text-slate-900 dark:text-white">
              {synthesis.status === 'revising' ? 'Revising…' : 'Synthesising…'}
            </h3>
          </div>
          <StreamPreview content={streamContent} />
        </Card>
      )}

      {synthesis.output && displayed && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <h2 className="font-semibold text-slate-900 dark:text-white">
              Synthesized Problem
            </h2>
            <Badge variant="outline" size="sm">
              rev {isViewingHistorical ? selectedRevision : synthesis.revision}
              {isViewingHistorical && ' (historical)'}
            </Badge>
            {sortedHistory.length > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <label className="text-xs text-slate-500">Revision:</label>
                <select
                  value={String(selectedRevision)}
                  aria-label="Synthesis revision"
                  title="Synthesis revision"
                  onChange={(e) => {
                    const v = e.target.value
                    setSelectedRevision(v === 'current' ? 'current' : Number(v))
                  }}
                  className="text-sm border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 bg-white dark:bg-slate-800"
                >
                  <option value="current">rev {synthesis.revision} (current)</option>
                  {sortedHistory.map((h) => (
                    <option key={h.revision} value={String(h.revision)}>
                      rev {h.revision}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <Section title="Current Undesirable Situation">
            <Markdown className="text-sm text-slate-700 dark:text-slate-200">
              {displayed.currentUndesirableSituation}
            </Markdown>
          </Section>

          <Section title="Future Desired Situation">
            <Markdown className="text-sm text-slate-700 dark:text-slate-200">
              {displayed.futureDesiredSituation}
            </Markdown>
          </Section>

          <Section title="AI3C">
            <ul className="text-sm text-slate-700 dark:text-slate-200 space-y-1">
              <li><b>Activity:</b> {displayed.ai3c?.activity}</li>
              <li><b>Item:</b> {displayed.ai3c?.item}</li>
              <li><b>Contradiction:</b> {displayed.ai3c?.contradiction}</li>
              <li><b>Constraints:</b> {displayed.ai3c?.constraints?.join('; ') || '—'}</li>
              <li><b>Criteria:</b> {displayed.ai3c?.criteria?.join('; ') || '—'}</li>
            </ul>
          </Section>

          {displayed.problemStatements && displayed.problemStatements.length > 0 && (
            <Section title="Problem Statements">
              <ol className="text-sm text-slate-700 dark:text-slate-200 space-y-2 list-decimal pl-5">
                {displayed.problemStatements.map((ps, i) => (
                  <li key={i}>
                    <Markdown className="text-sm">{ps.statement}</Markdown>
                    {ps.designDirections && ps.designDirections.length > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        Directions: {ps.designDirections.join('; ')}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            </Section>
          )}

          {displayed.narrativeSummary && (
            <Section title="Narrative">
              <Markdown className="text-sm text-slate-700 dark:text-slate-200">
                {displayed.narrativeSummary}
              </Markdown>
            </Section>
          )}
        </Card>
      )}

      {synthesis.output && (
        <Card className="p-5">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
            Request a revision
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
            OAI will update this same synthesis card with your feedback
            (rev {synthesis.revision} → rev {synthesis.revision + 1}). Older
            revisions stay accessible in the dropdown above.
          </p>
          <textarea
            className="textarea-field min-h-[80px] w-full"
            placeholder="Describe the change you'd like (e.g. 'Make the criteria more quantitative')"
            value={revision}
            onChange={(e) => setRevision(e.target.value)}
            disabled={isViewingHistorical || isBusy}
          />
          <div className="flex justify-end mt-3">
            <Button
              leftIcon={<RotateCcw className="w-4 h-4" />}
              onClick={async () => {
                if (!revision.trim()) return
                try {
                  await onRevise(revision.trim())
                  setRevision('')
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Revision failed')
                }
              }}
              disabled={!revision.trim() || isStreaming || isViewingHistorical || isBusy}
              isLoading={isStreaming || isBusy}
            >
              Revise
            </Button>
          </div>
        </Card>
      )}

      {synthesis.output && (
        <Card className="p-5">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-light-icy-blue dark:text-dark-muted-teal" />
            Continue exploring with OAI
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            Ask follow-up questions about this synthesis. Conversation is
            saved on this card so it persists across reloads.
          </p>

          {(synthesis.oaiChat?.length ?? 0) > 0 && (
            <div className="space-y-3 mb-3 max-h-[420px] overflow-y-auto pr-1">
              {synthesis.oaiChat?.map((m, i) => (
                <div
                  key={i}
                  className={cn(
                    'rounded-2xl p-3 text-sm',
                    m.role === 'user'
                      ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                      : 'bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-slate-800 dark:text-amber-50',
                  )}
                >
                  <div className="text-[10px] uppercase tracking-wide opacity-70 mb-1">
                    {m.role === 'user' ? 'You' : 'OAI'}
                  </div>
                  <Markdown className="text-sm">{m.content}</Markdown>
                </div>
              ))}
            </div>
          )}

          <textarea
            className="textarea-field min-h-[60px] w-full"
            placeholder="e.g. 'Walk me through the strongest cause-effect chain in more detail'"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            disabled={chatSubmitting || isBusy}
          />
          <div className="flex justify-end mt-3">
            <Button
              variant="secondary"
              leftIcon={<Send className="w-4 h-4" />}
              onClick={async () => {
                if (!chatInput.trim()) return
                setChatSubmitting(true)
                try {
                  await onContinueChat(chatInput.trim())
                  setChatInput('')
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Send failed')
                } finally {
                  setChatSubmitting(false)
                }
              }}
              disabled={!chatInput.trim() || chatSubmitting || isBusy}
              isLoading={chatSubmitting}
            >
              Ask OAI
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}

// ---------- Shared atoms ---------- //

const toneGradients: Record<string, string> = {
  human: 'from-slate-400 to-slate-600',
  cleaner: 'bg-agent-cleaner',
  qai: 'bg-agent-qai',
  aai: 'from-rose-400 to-pink-600',
  oai: 'from-amber-400 to-orange-600',
  system: 'from-slate-300 to-slate-500',
  mint: 'from-light-frosted-mint to-light-icy-blue dark:from-dark-muted-teal dark:to-light-icy-blue',
}

const AgentAvatar = ({
  tone,
  icon,
  small,
}: {
  tone: string
  icon: React.ReactNode
  small?: boolean
}) => {
  const cls = toneGradients[tone] ?? toneGradients.system
  const size = small ? 'w-8 h-8' : 'w-10 h-10'
  return (
    <div
      className={`${size} rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${cls}`}
    >
      {icon}
    </div>
  )
}

const StreamPreview = ({ content }: { content: string }) => {
  if (!content) {
    return <Loader variant="dots" text="Thinking..." />
  }
  return (
    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 max-h-64 overflow-y-auto">
      <Markdown className="text-sm text-slate-700 dark:text-slate-300">
        {content}
      </Markdown>
    </div>
  )
}

function getStateVariant(
  state: ConversationState,
): 'default' | 'blue' | 'mint' | 'pink' | 'yellow' | 'red' {
  switch (state) {
    case 'IDLE':
      return 'default'
    case 'CLEANING':
    case 'QUESTIONING':
    case 'GENERATING':
    case 'REVISING':
      return 'blue'
    case 'ANSWERING':
    case 'AAI_ASSIST':
      return 'pink'
    case 'CONFIRM_CLEAN':
    case 'EVALUATING':
    case 'READY_TO_GENERATE':
      return 'yellow'
    case 'REVIEWING':
    case 'COMPLETED':
      return 'mint'
    case 'ERROR':
      return 'red'
    default:
      return 'default'
  }
}

export default WorkspacePage
